import { sanitizeRichTextHtml } from './utils/sanitizeHtml';
//ProofreadTabContent.tsx
import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Tooltip, message, Radio, Segmented, theme, Modal, Checkbox } from 'antd';
import { ExclamationCircleOutlined, CopyOutlined, DeleteOutlined, InfoCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { diffWordsWithSpace, diffLines, Change } from 'diff';
import { formatFrenchNarrativeStyle } from './utils/frenchToneHelpers';
import type { ProofreadChangeSet, ProofreadProposal } from './types';
import { getAuthHeaders } from './utils/authHeaders';
import { getSavedProofreadTextForRow } from './utils/proofreadState';
import { isLocalFrontendHostname } from './utils/apiBase';
import { recordUxEvent } from './utils/uxTelemetry';

interface Chapter {
  chapter_guid: string;
  chapter_name: string;
  content: string;
  proofread_chapter: string;
  proofread_comments: string;
  proofread_word_count: number;
  order: number;
  word_count: number;
  rejected_content?: boolean;
  proofread_model?: string;
  proofread_at?: string;
  proofread_narrative_style?: string;
  proofread_mode?: string;
  proofread_change_set_id?: string | null;
  proofread_change_set_status?: string | null;
  proofread_change_set_updated_at?: string | null;
  proofread_change_set_applied_at?: string | null;
  proofread_change_set_summary?: Record<string, any> | null;
  proofread_generation?: number;
  proofread_stale?: boolean;
  proofread_stale_reason?: string | null;
  proofread_stale_at?: string | null;
}

interface TranslatedChapter {
  chapter_guid: string;
  translated_text: string;
  proofread_chapter?: string;
  proofread_text: string;
  proofread_comments: string;
  translated_word_count: number;
  proofread_word_count: number;
  source_language: string;
  target_language: string;
  api_model: string;
  translated_at?: string;
  narrative_style?: string;
  proofread_model?: string;
  proofread_at?: string;
  proofread_narrative_style?: string;
  proofread_mode?: string;
  proofread_change_set_id?: string | null;
  proofread_change_set_status?: string | null;
  proofread_change_set_updated_at?: string | null;
  proofread_change_set_applied_at?: string | null;
  proofread_change_set_summary?: Record<string, any> | null;
  proofread_generation?: number;
  proofread_stale?: boolean;
  proofread_stale_reason?: string | null;
  proofread_stale_at?: string | null;
}

// Temporarily hidden in the app; tests still exercise this diagnostic path.
const ADMIN_STAGE_TRACE_UI_ENABLED =
  process.env.NODE_ENV === 'test' &&
  isLocalFrontendHostname(
    typeof window !== 'undefined' ? window.location.hostname : undefined,
  );

interface Book {
  title: string;
  author: string;
  series: string;
  booknumber: number;
  coverUrl: string;
  chapters: Chapter[];
  genres: string[];
  [key: string]: any; // For dynamic translation keys
}

interface DiffPart {
  added?: boolean;
  removed?: boolean;
  value: string;
}

interface ProofreadTabContentProps {
  selectedChapter: Chapter | null;
  book: Book;
  sourceLang: string;
  targetLang: string;
  bookId?: string;
  apiBase?: string;
  isAdmin?: boolean;
  viewMode: string;
  setViewMode: (mode: string) => void;
  handleDeleteSingleProofreadChapter: (sourceLang: string, targetLang: string) => void;
  deleteEditingNotes: (chapterGuid: string, sourceLang: string, targetLang: string) => void;
  fetchProofreadChangeSet?: (params: {
    sourceLang: string;
    targetLang: string;
    chapterGuid: string;
  }) => Promise<ProofreadChangeSet | null>;
  applyProofreadChangeSet?: (params: {
    sourceLang: string;
    targetLang: string;
    chapterGuid: string;
    changeSetId: string;
    acceptedProposalIds: string[];
  }) => Promise<{ status?: string; message?: string } | void>;
  discardProofreadChangeSet?: (params: {
    sourceLang: string;
    targetLang: string;
    chapterGuid: string;
    changeSetId: string;
  }) => Promise<{ status?: string; message?: string } | void>;
  onOpenProofreadModal?: (params: {
    sourceLang: string;
    targetLang: string;
    proofread?: boolean;
    editingNotes?: boolean;
  }) => void;
  tabActionsContainer?: HTMLElement | null;
  isActive?: boolean;
  includeEditingNotesInContent?: boolean;
  onRefreshProofreadContent?: () => Promise<void> | void;
}

interface ProofreadTabContentResult {
  content: JSX.Element;
  isProofread: boolean;
  editingNotesContent?: React.ReactNode;
  hasEditingNotes?: boolean;
}

interface ProofreadStageTraceResponse {
  traceSummary?: Record<string, any> | null;
  changeAudit?: Record<string, any> | null;
  eventCount?: number;
  events?: Array<Record<string, any>>;
  changeSetId?: string;
  collectionPath?: string;
}

type AdminCopySection = {
  key: string;
  label: string;
  value: string;
  available: boolean;
  category: 'base' | 'rules' | 'responses';
};

const ADMIN_COPY_SECTION_DIVIDER = '='.repeat(60);

const toFiniteNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatUsdCompact = (value: number): string => {
  const normalized = toFiniteNumber(value);
  return `$${normalized.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
  })}`;
};

const getEventTokenCounts = (event: Record<string, any>): Record<string, any> => {
  const raw = (event?.token_counts || event?.tokenCounts || {}) as Record<string, any>;
  return raw && typeof raw === 'object' ? raw : {};
};

const getEventTokenUsageTotals = (event: Record<string, any>): {
  input: number;
  output: number;
  cached: number;
} => {
  const tokenCounts = getEventTokenCounts(event);
  return {
    input: toFiniteNumber(
      tokenCounts.inputTokens ?? tokenCounts.input_tokens ?? tokenCounts.prompt_tokens ?? tokenCounts.input,
    ),
    output: toFiniteNumber(
      tokenCounts.outputTokens ?? tokenCounts.output_tokens ?? tokenCounts.completion_tokens ?? tokenCounts.output,
    ),
    cached: toFiniteNumber(
      tokenCounts.cachedTokens ?? tokenCounts.cached_tokens ?? tokenCounts.cached ?? tokenCounts.cached_input_tokens,
    ),
  };
};

const hasUnpricedTokenUsage = (event: Record<string, any>): boolean => {
  const usage = getEventTokenUsageTotals(event);
  const hasBillableUsage = usage.input > 0 || usage.output > 0 || usage.cached > 0;
  if (!hasBillableUsage) {
    return false;
  }
  const costBreakdown = (event?.cost_breakdown || event?.costBreakdown || null) as Record<string, any> | null;
  const costUsd = (costBreakdown?.costUsd || costBreakdown?.cost_usd || null) as Record<string, any> | null;
  if (!costUsd || typeof costUsd !== 'object') {
    return true;
  }
  const totalCost = toFiniteNumber(costUsd.total);
  const inputCost = toFiniteNumber(costUsd.input);
  const outputCost = toFiniteNumber(costUsd.output);
  const cachedInputCost = toFiniteNumber(costUsd.cachedInput ?? costUsd.cached_input);
  return totalCost <= 0 && inputCost <= 0 && outputCost <= 0 && cachedInputCost <= 0;
};

const buildStageCostSummary = (event: Record<string, any>): string => {
  const costBreakdown = (event?.cost_breakdown || event?.costBreakdown || {}) as Record<string, any>;
  const costUsd = (costBreakdown?.costUsd || costBreakdown?.cost_usd || {}) as Record<string, any>;
  const inputCost = toFiniteNumber(costUsd.input);
  const cachedInputCost = toFiniteNumber(costUsd.cachedInput ?? costUsd.cached_input);
  const outputCost = toFiniteNumber(costUsd.output);
  const reasoningOutputCost = toFiniteNumber(costUsd.reasoningOutput ?? costUsd.reasoning_output);
  const nonReasoningOutputCost = toFiniteNumber(
    costUsd.nonReasoningOutput ?? costUsd.non_reasoning_output ?? costUsd.outputNonReasoning,
  );
  const totalCost = toFiniteNumber(costUsd.total || inputCost + cachedInputCost + outputCost);

  if (totalCost <= 0 && inputCost <= 0 && outputCost <= 0 && cachedInputCost <= 0) {
    return '';
  }

  const cachedSegment =
    cachedInputCost > 0 ? ` (+ ${formatUsdCompact(cachedInputCost)} cached input)` : '';
  const outputDetailSegment =
    reasoningOutputCost > 0 || nonReasoningOutputCost > 0
      ? ` (${formatUsdCompact(reasoningOutputCost)} reasoning, ${formatUsdCompact(nonReasoningOutputCost)} output)`
      : '';

  return `${formatUsdCompact(inputCost)} input${cachedSegment} - ${formatUsdCompact(outputCost)} output${outputDetailSegment} - ${formatUsdCompact(totalCost)} total`;
};

const formatAdminTraceValue = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value.trim();
  try {
    return JSON.stringify(value, null, 2).trim();
  } catch (_err) {
    return String(value).trim();
  }
};

const getEventTextField = (event: Record<string, any>, keys: string[]): string => {
  for (const key of keys) {
    const value = event?.[key];
    if (value === undefined || value === null) {
      continue;
    }
    const text = String(value || '').trim();
    if (text) {
      return text;
    }
  }
  return '';
};

const getFirstTraceTextByStage = (
  events: Array<Record<string, any>>,
  stageNames: string[],
  fieldKeys: string[],
): string => {
  const normalizedStageNames = Array.from(
    new Set(stageNames.map((name) => String(name || '').trim().toLowerCase())),
  );
  for (const event of events || []) {
    const stage = String(event?.stage || '').trim().toLowerCase();
    const isStageMatch = normalizedStageNames.some(
      (candidate) =>
        stage === candidate ||
        stage.startsWith(`${candidate}_`) ||
        stage.startsWith(`${candidate}(`) ||
        stage.startsWith(`${candidate} `),
    );
    if (!isStageMatch) {
      continue;
    }
    const text = getEventTextField(event, fieldKeys);
    if (text) {
      return text;
    }
  }
  return '';
};

const getAllTraceResponsesByStage = (
  events: Array<Record<string, any>>,
  stageNames: string[],
): string => {
  const normalizedStageNames = Array.from(
    new Set(stageNames.map((name) => String(name || '').trim().toLowerCase())),
  );
  const formattedEntries: string[] = [];
  let sequence = 1;

  const pushFormattedEntry = (
    value: unknown,
    meta?: { run?: unknown; pass?: unknown; phase?: unknown; status?: unknown },
  ) => {
    const payload = formatAdminTraceValue(value);
    if (!payload) return;
    const tags: string[] = [];
    if (meta?.run !== undefined && meta?.run !== null && String(meta.run).trim()) {
      tags.push(`run=${String(meta.run).trim()}`);
    }
    if (meta?.pass !== undefined && meta?.pass !== null && String(meta.pass).trim()) {
      tags.push(`pass=${String(meta.pass).trim()}`);
    }
    if (meta?.phase !== undefined && meta?.phase !== null && String(meta.phase).trim()) {
      tags.push(`phase=${String(meta.phase).trim()}`);
    }
    if (meta?.status !== undefined && meta?.status !== null && String(meta.status).trim()) {
      tags.push(`status=${String(meta.status).trim()}`);
    }
    const header = tags.length > 0 ? `[response ${sequence} | ${tags.join(' | ')}]` : `[response ${sequence}]`;
    formattedEntries.push(`${header}\n${payload}`);
    sequence += 1;
  };

  for (const event of events || []) {
    const stage = String(event?.stage || '').trim().toLowerCase();
    const isStageMatch = normalizedStageNames.some(
      (candidate) =>
        stage === candidate ||
        stage.startsWith(`${candidate}_`) ||
        stage.startsWith(`${candidate}(`) ||
        stage.startsWith(`${candidate} `),
    );
    if (!isStageMatch) continue;

    const rawResponses = event?.raw_responses ?? event?.rawResponses;
    if (Array.isArray(rawResponses) && rawResponses.length > 0) {
      rawResponses.forEach((entry) => {
        if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
          pushFormattedEntry(
            (entry as Record<string, unknown>).raw_response
              ?? (entry as Record<string, unknown>).rawResponse
              ?? entry,
            {
              run:
                (entry as Record<string, unknown>).run_index
                ?? (entry as Record<string, unknown>).runIndex,
              pass:
                (entry as Record<string, unknown>).pass_index
                ?? (entry as Record<string, unknown>).passIndex,
              phase: (entry as Record<string, unknown>).phase,
              status: (entry as Record<string, unknown>).status ?? event?.status,
            },
          );
        } else {
          pushFormattedEntry(entry);
        }
      });
      continue;
    }

    const fallbackRaw = event?.raw_response ?? event?.rawResponse;
    if (fallbackRaw !== undefined && fallbackRaw !== null) {
      pushFormattedEntry(fallbackRaw, { status: event?.status });
    }
  }

  return formattedEntries.join('\n\n');
};

// Helper function to remove hyperlinks
export const removeHyperlinks = (htmlContent: string | undefined | null): string => {
  const input = sanitizeRichTextHtml(htmlContent);
  const sanitizedInput = input
    // Chapter payloads can include embedded document/head/style markup from EPUB HTML.
    // Strip it for display so malformed or dark-theme-hostile CSS cannot blank the viewer.
    .replace(/<head\b[^>]*>[\s\S]*?<\/head>/gi, '')
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
    // Some malformed payloads contain an opening <style> block without a valid closing tag.
    // Remove the opening tag plus the CSS payload up to the next HTML tag so the chapter text survives.
    .replace(/<style\b[^>]*>[^<]*(?=<|$)/gi, '')
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<noscript\b[^>]*>[\s\S]*?<\/noscript>/gi, '')
    .replace(/<\/?(html|body)\b[^>]*>/gi, '')
    .replace(/<meta\b[^>]*\/?>/gi, '')
    .replace(/<link\b[^>]*\/?>/gi, '')
    // Some model outputs include malformed pseudo-tags like `< style="...">` as raw text.
    // Strip them before HTML parsing so they don't render in chapter content.
    .replace(/<\s+style\b[^>]*>/gi, '')
    .replace(/<\s*\/\s*style\s*>/gi, '');

  // During SSR/testing there may be no document available; return untouched HTML.
  if (typeof document === 'undefined') {
    return sanitizedInput;
  }

  const container = document.createElement('div');
  container.innerHTML = sanitizedInput;
  container.querySelectorAll('a').forEach(link => {
    link.removeAttribute('href');
    (link as HTMLElement).style.pointerEvents = 'none';
    (link as HTMLElement).style.color = 'inherit';
    (link as HTMLElement).style.textDecoration = 'none';
  });

  container.querySelectorAll('[style]').forEach(element => {
    const el = element as HTMLElement;
    if (!el.style) return;
    if (el.style.color) {
      el.style.removeProperty('color');
    }
    if ((el.style as any).webkitTextFillColor) {
      el.style.removeProperty('-webkit-text-fill-color');
    }
    if ((el.style as any).webkitTextStrokeColor) {
      el.style.removeProperty('-webkit-text-stroke-color');
    }
  });

  container.querySelectorAll('[color]').forEach(element => {
    element.removeAttribute('color');
  });

  return container.innerHTML;
};

// Helper function to synchronize scrolling between panels
const syncScroll = (e: React.UIEvent<HTMLDivElement>, otherPanelId: string) => {
  const otherPanel = document.getElementById(otherPanelId) as HTMLDivElement;
  if (otherPanel) {
    otherPanel.scrollTop = e.currentTarget.scrollTop;
  }
};

// Helper function to copy proofread content
const copyProofreadToClipboard = (proofreadText: string) => {
  const dummyElement = document.createElement('div');
  dummyElement.innerHTML = removeHyperlinks(proofreadText);

  // Make sure the dummy element is not displayed
  dummyElement.style.position = 'absolute';
  dummyElement.style.left = '-9999px';
  document.body.appendChild(dummyElement);

  const range = document.createRange();
  range.selectNode(dummyElement);

  const selection = window.getSelection();
  if (selection) {
    selection.removeAllRanges();
    selection.addRange(range);

    try {
      document.execCommand('copy');
      message.success('Proofread content copied to clipboard');
    } catch (err) {
      message.error('Failed to copy proofread content to clipboard');
    }

    selection.removeAllRanges();
  } else {
    message.error('Failed to access clipboard');
  }

  // Remove the dummy element from the DOM
  document.body.removeChild(dummyElement);
};

const decodeEntities = (value: string): string => {
  if (typeof document === 'undefined') {
    return value;
  }
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

const serializeHtmlNodeToText = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || '';
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }

  const element = node as HTMLElement;
  const tag = element.tagName.toLowerCase();

  if (tag === 'style' || tag === 'script' || tag === 'noscript') {
    return '';
  }
  if (tag === 'br') {
    return '\n';
  }

  const childText = Array.from(element.childNodes)
    .map((child) => serializeHtmlNodeToText(child))
    .join('');

  if (tag === 'li') {
    return `- ${childText}\n`;
  }
  if (tag === 'td' || tag === 'th') {
    return `${childText}\t`;
  }
  if (tag === 'tr') {
    return `${childText}\n`;
  }
  if (tag === 'p' || tag === 'div' || tag === 'section' || tag === 'article' || tag === 'blockquote') {
    return `${childText}\n\n`;
  }
  if (tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4' || tag === 'h5' || tag === 'h6') {
    return `${childText}\n\n`;
  }
  if (tag === 'ul' || tag === 'ol' || tag === 'table' || tag === 'pre') {
    return `${childText}\n`;
  }

  return childText;
};

const htmlToDiffText = (html: string): string => {
  const input = removeHyperlinks(String(html || ''));
  if (typeof document === 'undefined') {
    const normalizedHtml = input
      .replace(/<head[\s\S]*?<\/head>/gi, '')
      .replace(/^[\s\S]*?<body[^>]*>/i, '')
      .replace(/<\/body>[\s\S]*$/i, '')
      .replace(/<\s*br\s*\/?\s*>/gi, '\n')
      .replace(/<\/\s*p\s*>/gi, '\n\n')
      .replace(/<\s*p\b[^>]*>/gi, '')
      .replace(/<\/\s*(div|li|tr|h[1-6]|blockquote|pre)\s*>/gi, '\n')
      .replace(/<\s*(div|li|tr|h[1-6]|blockquote|pre)\b[^>]*>/gi, '')
      .replace(/<[^>]+>/g, '');
    return decodeEntities(normalizedHtml);
  }

  let rootNodes: NodeListOf<ChildNode> | ChildNode[] = [];
  try {
    const parser = new DOMParser();
    const parsed = parser.parseFromString(input, 'text/html');
    rootNodes = parsed.body?.childNodes || [];
  } catch (_error) {
    const container = document.createElement('div');
    container.innerHTML = input;
    rootNodes = container.childNodes;
  }

  return Array.from(rootNodes)
    .map((child) => serializeHtmlNodeToText(child))
    .join('');
};

const normalizeText = (text: string): string => {
  return text
    .replace(/[‘’′]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\.{3}/g, '…')
    .replace(/\u00a0/g, ' ')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const renderDiffToken = (text: string, className?: string): string => {
  const escaped = escapeHtml(text)
    .replace(/\n{2,}/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
  if (!className) return escaped;
  return `<span class="${className}">${escaped}</span>`;
};

const renderDiff = (originalText: string, proofreadText: string): string => {
  const oldStr = normalizeText(htmlToDiffText(originalText));
  const newStr = normalizeText(htmlToDiffText(proofreadText));
  const lineDiff = diffLines(oldStr, newStr);
  const chunks: string[] = [];

  for (let i = 0; i < lineDiff.length; i += 1) {
    const current = lineDiff[i];
    const next = lineDiff[i + 1];

    if (current?.removed && next?.added) {
      const wordDiff = diffWordsWithSpace(current.value, next.value);
      wordDiff.forEach((part: Change) => {
        if (part.added) {
          chunks.push(renderDiffToken(part.value, 'proofread-inline-diff-add'));
        } else if (part.removed) {
          chunks.push(renderDiffToken(part.value, 'proofread-inline-diff-remove'));
        } else {
          chunks.push(renderDiffToken(part.value));
        }
      });
      i += 1;
      continue;
    }

    if (current?.added) {
      chunks.push(renderDiffToken(current.value, 'proofread-inline-diff-add'));
      continue;
    }
    if (current?.removed) {
      chunks.push(renderDiffToken(current.value, 'proofread-inline-diff-remove'));
      continue;
    }
    chunks.push(renderDiffToken(current?.value || ''));
  }

  return chunks.join('');
};

const isJapaneseLanguage = (lang?: string) => {
  if (!lang) return false;
  const trimmed = lang.trim();
  const lower = trimmed.toLowerCase();
  return (
    lower.includes('japanese') ||
    lower.startsWith('ja') ||
    lower.startsWith('jp') ||
    trimmed.includes('日本語')
  );
};

const containsJapaneseCharacters = (text?: string) => {
  if (!text) return false;
  return /[\u3040-\u30ff\u31f0-\u31ff\u3400-\u4dbf\u4e00-\u9fff\uF900-\uFAFF\uFF66-\uFF9F]/.test(text);
};

const countSubstringOccurrences = (text: string, token: string): number => {
  if (!token) return 0;
  let count = 0;
  let cursor = 0;
  while (cursor <= text.length) {
    const idx = text.indexOf(token, cursor);
    if (idx < 0) break;
    count += 1;
    cursor = idx + token.length;
  }
  return count;
};

const sortProposals = (proposals: ProofreadProposal[]): ProofreadProposal[] => {
  return [...proposals].sort((a, b) => {
    const stageA = Number(a.stage_index || 0);
    const stageB = Number(b.stage_index || 0);
    if (stageA !== stageB) return stageA - stageB;
    const orderA = Number(a.order_in_stage || 0);
    const orderB = Number(b.order_in_stage || 0);
    return orderA - orderB;
  });
};

const resolveAcceptedProposalIds = (
  proposals: ProofreadProposal[],
  acceptedIds: Set<string>,
): Set<string> => {
  const proposalById = new Map<string, ProofreadProposal>();
  proposals.forEach((proposal) => {
    if (proposal?.proposal_id) {
      proposalById.set(proposal.proposal_id, proposal);
    }
  });
  const resolved = new Set<string>();
  acceptedIds.forEach((proposalId) => {
    if (proposalById.has(proposalId)) {
      resolved.add(proposalId);
    }
  });

  Array.from(resolved).forEach((proposalId) => {
    const proposal = proposalById.get(proposalId);
    if (!proposal) return;
    (proposal.supersedes || []).forEach((supersededId) => {
      if (supersededId && resolved.has(supersededId)) {
        resolved.delete(supersededId);
      }
    });
    const supersededById = String(proposal.superseded_by || '').trim();
    if (supersededById && resolved.has(supersededById)) {
      resolved.delete(proposalId);
    }
  });

  return resolved;
};

const expandAcceptedProposalIdsForApply = (
  proposals: ProofreadProposal[],
  acceptedIds: Set<string>,
): Set<string> => {
  const proposalById = new Map<string, ProofreadProposal>();
  proposals.forEach((proposal) => {
    const proposalId = String(proposal.proposal_id || '').trim();
    if (proposalId) {
      proposalById.set(proposalId, proposal);
    }
  });
  const expanded = new Set<string>();
  const queue = Array.from(acceptedIds);
  queue.forEach((proposalId) => {
    if (proposalById.has(proposalId)) {
      expanded.add(proposalId);
    }
  });
  while (queue.length > 0) {
    const proposalId = queue.pop() as string;
    const proposal = proposalById.get(proposalId);
    if (!proposal) continue;
    (proposal.supersedes || []).forEach((supersededId) => {
      const sid = String(supersededId || '').trim();
      if (!sid || expanded.has(sid) || !proposalById.has(sid)) return;
      expanded.add(sid);
      queue.push(sid);
    });
  }
  return expanded;
};

const buildConflictGroups = (proposals: ProofreadProposal[]) => {
  const reviewable = proposals.filter((proposal) => proposal.reviewable);
  const proposalById = new Map<string, ProofreadProposal>();
  const adjacency = new Map<string, Set<string>>();
  reviewable.forEach((proposal) => {
    const proposalId = String(proposal.proposal_id || '').trim();
    if (!proposalId) return;
    proposalById.set(proposalId, proposal);
    adjacency.set(proposalId, new Set<string>());
  });

  reviewable.forEach((proposal) => {
    const proposalId = String(proposal.proposal_id || '').trim();
    if (!proposalId) return;
    const neighbors = adjacency.get(proposalId);
    if (!neighbors) return;
    (proposal.supersedes || []).forEach((supersededId) => {
      const sid = String(supersededId || '').trim();
      if (!sid || !adjacency.has(sid)) return;
      neighbors.add(sid);
      adjacency.get(sid)?.add(proposalId);
    });
    const supersededById = String(proposal.superseded_by || '').trim();
    if (supersededById && adjacency.has(supersededById)) {
      neighbors.add(supersededById);
      adjacency.get(supersededById)?.add(proposalId);
    }
  });

  const visited = new Set<string>();
  const groups: Array<{ groupId: string; proposals: ProofreadProposal[] }> = [];
  const proposalToGroup = new Map<string, string>();
  let counter = 0;

  proposalById.forEach((_proposal, proposalId) => {
    if (visited.has(proposalId)) return;
    const stack = [proposalId];
    const component: string[] = [];
    visited.add(proposalId);
    while (stack.length) {
      const current = stack.pop() as string;
      component.push(current);
      (adjacency.get(current) || new Set<string>()).forEach((neighbor) => {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          stack.push(neighbor);
        }
      });
    }
    counter += 1;
    const groupId = `group_${counter}`;
    const groupProposals = sortProposals(
      component
        .map((proposalIdInGroup) => proposalById.get(proposalIdInGroup))
        .filter(Boolean) as ProofreadProposal[],
    );
    groupProposals.forEach((proposalInGroup) => {
      proposalToGroup.set(proposalInGroup.proposal_id, groupId);
    });
    groups.push({ groupId, proposals: groupProposals });
  });

  return { groups, proposalToGroup };
};

const buildPreviewFromAcceptedProposals = (
  baseText: string,
  proposals: ProofreadProposal[],
  acceptedIds: Set<string>,
): { previewText: string; error?: string } => {
  const resolvedAcceptedIds = resolveAcceptedProposalIds(proposals, acceptedIds);
  const applyIds = expandAcceptedProposalIdsForApply(proposals, resolvedAcceptedIds);
  const ordered = sortProposals(
    proposals.filter(
      (proposal) =>
        proposal.reviewable && applyIds.has(String(proposal.proposal_id || '').trim()),
    ),
  );
  let workingText = String(baseText || '');
  for (const proposal of ordered) {
    const sourceText = String(proposal.source_text || '');
    const targetText = String(proposal.target_text || '');
    if (!sourceText || !targetText) continue;
    const matches = countSubstringOccurrences(workingText, sourceText);
    if (matches !== 1) {
      return {
        previewText: workingText,
        error: `Could not uniquely apply proposal ${proposal.proposal_id}.`,
      };
    }
    workingText = workingText.replace(sourceText, targetText);
  }
  return { previewText: workingText };
};

interface ReviewRegion {
  regionId: string;
  groupId: string;
  start: number;
  end: number;
  sourceText: string;
  proposals: ProofreadProposal[];
  hasStructuralHtml: boolean;
}

type ReviewRegionVisualState = 'pending' | 'preview' | 'accepted' | 'rejected';

declare global {
  interface Window {
    __dumpProofreadReviewHtml?: () => void;
  }
}

const formatReviewDisplayText = (value: string): string =>
  normalizeText(htmlToDiffText(String(value || '')));

const hasStructuralHtml = (value: string): boolean => {
  const html = String(value || '').toLowerCase();
  if (!html.includes('<')) return false;
  if (/<(ul|ol|li|table|tr|td|th|pre)\b|<br\s*\/?>/.test(html)) return true;
  const blockOpenTags = html.match(/<(p|div|section|article|blockquote|h[1-6])\b/g) || [];
  return blockOpenTags.length > 1;
};

const renderReviewHtml = (value: string): { __html: string } => ({
  __html: removeHyperlinks(String(value || '')),
});

const getRegionVisualPalette = (
  token: Record<string, any>,
  state: ReviewRegionVisualState,
  selected: boolean,
) => {
  const paletteByState: Record<ReviewRegionVisualState, { background: string; border: string; text: string; shadow: string }> = {
    pending: {
      background: 'rgba(245, 158, 11, 0.24)',
      border: '#fbbf24',
      text: token.colorText || 'var(--text-main)',
      shadow: 'rgba(245, 158, 11, 0.22)',
    },
    preview: {
      background: token.colorPrimaryBg || 'rgba(59, 130, 246, 0.22)',
      border: token.colorPrimaryBorder || token.colorPrimary || '#60a5fa',
      text: token.colorText || 'var(--text-main)',
      shadow: 'rgba(59, 130, 246, 0.20)',
    },
    accepted: {
      background: 'rgba(34, 197, 94, 0.24)',
      border: '#4ade80',
      text: token.colorText || 'var(--text-main)',
      shadow: 'rgba(34, 197, 94, 0.20)',
    },
    rejected: {
      background: 'rgba(239, 68, 68, 0.22)',
      border: '#f87171',
      text: token.colorText || 'var(--text-main)',
      shadow: 'rgba(239, 68, 68, 0.18)',
    },
  };
  const palette = paletteByState[state];
  return {
    background: palette.background,
    border: selected ? (token.colorPrimary || '#60a5fa') : palette.border,
    text: palette.text,
    shadow: selected
      ? `0 0 0 1px ${token.colorPrimaryBorder || token.colorPrimary || '#60a5fa'}`
      : `inset 0 0 0 1px ${palette.border}, 0 0 0 1px ${palette.shadow}`,
  };
};

const locatePlainTextRange = (
  plainText: string,
  sourceText: string,
): { start: number; end: number } | null => {
  const normalizedSource = normalizeText(htmlToDiffText(sourceText || ''));
  if (!normalizedSource) return null;
  const start = plainText.indexOf(normalizedSource);
  if (start < 0) return null;
  if (countSubstringOccurrences(plainText, normalizedSource) !== 1) return null;
  return { start, end: start + normalizedSource.length };
};

const locatePlainTextRangeFromStoredLocation = (
  baseText: string,
  plainText: string,
  proposal: ProofreadProposal,
): { start: number; end: number } | null => {
  const locatedStart = proposal.located_start;
  const locatedEnd = proposal.located_end;
  if (typeof locatedStart !== 'number' || typeof locatedEnd !== 'number'
    || !Number.isInteger(locatedStart) || !Number.isInteger(locatedEnd)
    || locatedStart < 0 || locatedEnd <= locatedStart) {
    return null;
  }

  // Firestore locations use Python Unicode code points, not JS UTF-16 units.
  const codePoints = Array.from(baseText);
  const sourceText = String(proposal.source_text || '');
  if (locatedEnd > codePoints.length
    || codePoints.slice(locatedStart, locatedEnd).join('') !== sourceText) {
    return null;
  }

  // Render the whole document: parsing a truncated HTML prefix can invent closing
  // blocks, and trimming it drops the space/newlines before the actual change.
  // A temporary text marker preserves that boundary through the display pipeline.
  let marker = '\uE000proofread-anchor\uE001';
  while (baseText.includes(marker) || plainText.includes(marker)) marker += '_';
  const markedHtml = codePoints.slice(0, locatedStart).join('')
    + marker + codePoints.slice(locatedStart).join('');
  const markedText = htmlToDiffText(markedHtml);
  const markerIndex = markedText.indexOf(marker);
  if (markerIndex < 0 || markedText.indexOf(marker, markerIndex + marker.length) >= 0
    || normalizeText(markedText.replace(marker, '')) !== plainText) {
    return null;
  }

  const prefixWithMarker = normalizeText(markedText.slice(0, markerIndex) + marker);
  const start = prefixWithMarker.length - marker.length;
  const normalizedSource = formatReviewDisplayText(sourceText);
  const end = start + normalizedSource.length;
  // Never clamp a guessed range or replace unrelated visible text. Unique-source
  // lookup remains available to the caller for legacy or unmappable locations.
  if (!normalizedSource || plainText.slice(start, end) !== normalizedSource) return null;
  return { start, end };
};

const selectRegionAnchor = (
  baseText: string,
  plainBaseText: string,
  proposals: ProofreadProposal[],
): { start: number; end: number; sourceText: string } | null => {
  const ordered = sortProposals(proposals);
  for (const proposal of ordered) {
    const storedLocationRange = locatePlainTextRangeFromStoredLocation(baseText, plainBaseText, proposal);
    if (storedLocationRange) {
      return {
        ...storedLocationRange,
        sourceText: normalizeText(htmlToDiffText(String(proposal.source_text || ''))),
      };
    }
  }
  for (const proposal of ordered) {
    const range = locatePlainTextRange(plainBaseText, String(proposal.source_text || ''));
    if (range) {
      return {
        ...range,
        sourceText: normalizeText(htmlToDiffText(String(proposal.source_text || ''))),
      };
    }
  }
  return null;
};

const buildReviewRegions = (
  baseText: string,
  conflictGroups: Array<{ groupId: string; proposals: ProofreadProposal[] }>,
): { plainText: string; regions: ReviewRegion[] } => {
  const plainText = normalizeText(htmlToDiffText(baseText || ''));
  const regions = conflictGroups
    .map((group, index) => {
      const anchor = selectRegionAnchor(baseText, plainText, group.proposals);
      if (!anchor) return null;
      return {
        regionId: `region_${index + 1}`,
        groupId: group.groupId,
        start: anchor.start,
        end: anchor.end,
        sourceText: anchor.sourceText,
        proposals: sortProposals(group.proposals),
        hasStructuralHtml: group.proposals.some(
          (proposal) =>
            hasStructuralHtml(String(proposal.source_text || ''))
            || hasStructuralHtml(String(proposal.target_text || '')),
        ),
      } as ReviewRegion;
    })
    .filter(Boolean) as ReviewRegion[];

  regions.sort((a, b) => a.start - b.start);
  return { plainText, regions };
};

const getPreferredProposalIdForRegion = (
  region: ReviewRegion,
  acceptedIds: Set<string>,
): string => {
  const accepted = region.proposals.find((proposal) => acceptedIds.has(String(proposal.proposal_id || '')));
  if (accepted?.proposal_id) return String(accepted.proposal_id);
  const active = region.proposals.find((proposal) => proposal.active);
  if (active?.proposal_id) return String(active.proposal_id);
  return String(region.proposals[0]?.proposal_id || '');
};

const areStringSetsEqual = (left: Set<string>, right: Set<string>): boolean => {
  if (left.size !== right.size) return false;
  for (const value of left) {
    if (!right.has(value)) return false;
  }
  return true;
};

// Main function
const ProofreadTabContent = (props: ProofreadTabContentProps): ProofreadTabContentResult => {
  const {
    selectedChapter,
    book,
    sourceLang,
    targetLang,
    bookId,
    apiBase,
    isAdmin = false,
    viewMode,
    setViewMode,
    handleDeleteSingleProofreadChapter,
    deleteEditingNotes,
    fetchProofreadChangeSet,
    applyProofreadChangeSet,
    discardProofreadChangeSet,
    onOpenProofreadModal,
    tabActionsContainer,
    isActive = true,
    includeEditingNotesInContent = true,
    onRefreshProofreadContent,
  } = props;

  const [writingDirection, setWritingDirection] = useState<'horizontal' | 'vertical'>('horizontal');
  const [proposalChangeSet, setProposalChangeSet] = useState<ProofreadChangeSet | null>(null);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [proposalError, setProposalError] = useState('');
  const [proposalReloadNonce, setProposalReloadNonce] = useState(0);
  const [acceptedProposalIds, setAcceptedProposalIds] = useState<Set<string>>(new Set());
  const [previewProposalIds, setPreviewProposalIds] = useState<Set<string>>(new Set());
  const [rejectedRegionIds, setRejectedRegionIds] = useState<Set<string>>(new Set());
  const [stageTraceLoading, setStageTraceLoading] = useState(false);
  const [stageTraceError, setStageTraceError] = useState('');
  const [stageTraceSummary, setStageTraceSummary] = useState<Record<string, any> | null>(null);
  const [stageTraceChangeAudit, setStageTraceChangeAudit] = useState<Record<string, any> | null>(null);
  const [stageTraceEvents, setStageTraceEvents] = useState<Array<Record<string, any>>>([]);
  const [isAdminCopyModalOpen, setIsAdminCopyModalOpen] = useState(false);
  const [selectedAdminCopyKeys, setSelectedAdminCopyKeys] = useState<string[]>([]);
  const chapterGuid = selectedChapter?.chapter_guid || '';
  const [isApplyingProposals, setIsApplyingProposals] = useState(false);
  const [isDiscardingProposalReview, setIsDiscardingProposalReview] = useState(false);
  const [isSaveAcceptedConfirmationOpen, setIsSaveAcceptedConfirmationOpen] = useState(false);
  const [acceptedInitialized, setAcceptedInitialized] = useState(false);
  const [selectedRegionId, setSelectedRegionId] = useState('');
  const [reviewPanelOpen, setReviewPanelOpen] = useState(false);
  const [restoredDraftKey, setRestoredDraftKey] = useState('');
  const regionElementRefs = useRef<Record<string, HTMLElement | null>>({});
  const trackedReviewOpenRef = useRef('');
  const { token } = theme.useToken();
  const debugProofreadReview = useMemo(() => {
    try {
      return localStorage.getItem('debugProofreadModal') === 'true';
    } catch (_error) {
      return false;
    }
  }, []);
  const traceCopyButtonStyle: React.CSSProperties = {
    color: 'var(--text-main)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 4,
    width: 20,
    minWidth: 20,
    height: 20,
    padding: 0,
    background: 'var(--surface-base)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  };
  const topTraceCopyButtonStyle: React.CSSProperties = {
    color: 'var(--text-main)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 6,
    minWidth: 24,
    height: 24,
    padding: 0,
    background: 'var(--surface-base)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  };

  const formatDateTime = (iso?: string | null) => {
    if (!iso) return '';
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleString();
  };

  const copyTraceSectionToClipboard = useCallback(async (sectionLabel: string, text: string) => {
    const value = String(text || '');
    if (!value) {
      message.warning(`${sectionLabel} is empty.`);
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      message.success(`${sectionLabel} copied to clipboard.`);
    } catch (_err) {
      message.error(`Failed to copy ${sectionLabel.toLowerCase()}.`);
    }
  }, []);

  const buildStaleTooltip = (reason?: string | null, staleAt?: string | null) => {
    const reasonKey = (reason || '').toLowerCase();
    const primaryLine = reasonKey.includes('translation')
      ? 'The translation was edited after this proofread completed.'
      : reasonKey.includes('source')
        ? 'The source chapter was edited after this proofread completed.'
        : 'This proofread may be out of date.';
    const lines = [
      primaryLine,
      staleAt ? `Marked stale: ${staleAt}` : '',
      'Re-run proofreading to refresh this chapter.',
    ].filter(Boolean);
    return (
      <div>
        {lines.map((line, idx) => (
          <div key={`proofread-stale-${idx}`}>{line}</div>
        ))}
      </div>
    );
  };

  const contentStyle: CSSProperties = {
    color: 'var(--text-main)',
  };

  let proofreadContent = null;
  let originalText = "";
  let proofreadText = "";
  let editingNotesContent = null;
  let isProofread = true;

  let proofreadModel: string | undefined;
  let proofreadCompletedAt: string | undefined;
  let proofreadNarrativeStyle: string | undefined;
  let translatedChapter: TranslatedChapter | undefined;
  let proofreadCompletedOn = '';
  let proofreadMeta: string[] = [];
  let proofreadMetaRows: Array<{ label: string; value: string }> = [];
  let proofreadStaleBadge: JSX.Element | null = null;
  let proofreadMode: string | undefined;
  let proofreadChangeSetId: string | undefined;
  let proofreadChangeSetStatus: string | undefined;
  let proofreadChangeSetUpdatedAt: string | undefined;
  let proofreadGeneration: number | undefined;
  let normalizedProofreadMode = '';
  let normalizedProofreadChangeSetStatus = '';
  let shouldTreatAsNoChangeProofreadPass = false;
  let noChangeProofreadMessage = '';

  if (selectedChapter && sourceLang !== "Original") {
    const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
    const translatedChapters: TranslatedChapter[] = book[translatedChaptersKey];
    translatedChapter = translatedChapters?.find(chap => chap.chapter_guid === selectedChapter.chapter_guid);
  }

  if (selectedChapter) {
    if (sourceLang === "Original") {
      originalText = selectedChapter.content;
      proofreadText = selectedChapter.proofread_chapter;
      proofreadModel = selectedChapter.proofread_model || (selectedChapter as any)?.api_model || undefined;
      proofreadCompletedAt = selectedChapter.proofread_at || undefined;
      proofreadNarrativeStyle = formatFrenchNarrativeStyle(selectedChapter.proofread_narrative_style) || undefined;
      proofreadMode = selectedChapter.proofread_mode || undefined;
      proofreadChangeSetId = selectedChapter.proofread_change_set_id || undefined;
      proofreadChangeSetStatus = selectedChapter.proofread_change_set_status || undefined;
      proofreadChangeSetUpdatedAt = selectedChapter.proofread_change_set_updated_at || undefined;
      proofreadGeneration =
        Number(selectedChapter.proofread_generation)
        || Number(selectedChapter.proofread_change_set_summary?.proofread_generation)
        || undefined;
    } else {
      if (translatedChapter) {
        originalText = translatedChapter.translated_text;
        proofreadText = getSavedProofreadTextForRow(translatedChapter);
        proofreadModel = translatedChapter.proofread_model || translatedChapter.api_model || undefined;
        proofreadCompletedAt = translatedChapter.proofread_at || undefined;
        proofreadNarrativeStyle = formatFrenchNarrativeStyle(translatedChapter.proofread_narrative_style) || undefined;
        proofreadMode = translatedChapter.proofread_mode || undefined;
        proofreadChangeSetId = translatedChapter.proofread_change_set_id || undefined;
        proofreadChangeSetStatus = translatedChapter.proofread_change_set_status || undefined;
        proofreadChangeSetUpdatedAt = translatedChapter.proofread_change_set_updated_at || undefined;
        proofreadGeneration =
          Number(translatedChapter.proofread_generation)
          || Number(translatedChapter.proofread_change_set_summary?.proofread_generation)
          || undefined;
      }
    }

    const proofreadSummary = sourceLang === 'Original'
      ? selectedChapter.proofread_change_set_summary
      : translatedChapter?.proofread_change_set_summary;
    normalizedProofreadMode = String(proofreadMode || '').trim().toLowerCase();
    normalizedProofreadChangeSetStatus = String(proofreadChangeSetStatus || '').trim().toLowerCase();
    const hasExplicitReviewableCount =
      Boolean(proofreadSummary)
      && typeof proofreadSummary === 'object'
      && Object.prototype.hasOwnProperty.call(proofreadSummary, 'reviewable_count');
    const explicitReviewableCount = hasExplicitReviewableCount
      ? Number((proofreadSummary as Record<string, any>)?.reviewable_count)
      : undefined;
    shouldTreatAsNoChangeProofreadPass =
      Boolean(selectedChapter)
      && Boolean(proofreadChangeSetId)
      && (
        normalizedProofreadChangeSetStatus === 'completed_no_changes'
        || (
          normalizedProofreadMode === 'proposal'
          && typeof explicitReviewableCount === 'number'
          && Number.isFinite(explicitReviewableCount)
          && explicitReviewableCount <= 0
        )
      );
    const isProposalMode = normalizedProofreadMode === 'proposal';
    if (isProposalMode) {
      const summaryGeneration = Number(proofreadSummary?.proofread_generation) || undefined;
      const summaryModel = String(proofreadSummary?.model_display_name || '').trim() || undefined;
      if (summaryGeneration) {
        proofreadGeneration = summaryGeneration;
      }
      if (summaryModel) {
        proofreadModel = summaryModel;
      }
    }
    if (shouldTreatAsNoChangeProofreadPass && !String(proofreadText || '').trim() && String(originalText || '').trim()) {
      proofreadText = originalText;
    }
    const isFirstProofreadPass = !proofreadGeneration || proofreadGeneration <= 1;
    if (shouldTreatAsNoChangeProofreadPass) {
      noChangeProofreadMessage = isFirstProofreadPass
        ? (
          sourceLang === 'Original'
            ? 'The latest proofread pass found no reviewable changes. The proofread content below matches the current chapter text.'
            : 'The latest proofread pass found no reviewable changes. The proofread content below matches the current translation text.'
        )
        : 'The latest proofread pass found no reviewable changes. The saved proofread text below is unchanged from the previous pass.';
    }

    proofreadCompletedOn = formatDateTime(proofreadCompletedAt);
    proofreadMetaRows = [
      proofreadGeneration ? { label: 'Proofread Pass', value: String(proofreadGeneration) } : null,
      proofreadModel ? { label: 'Model', value: proofreadModel } : null,
      proofreadNarrativeStyle ? { label: 'Narrative Style', value: proofreadNarrativeStyle } : null,
    ].filter(Boolean) as Array<{ label: string; value: string }>;
    const isProofreadStale =
      sourceLang === 'Original'
        ? Boolean(selectedChapter.proofread_stale)
        : Boolean(translatedChapter?.proofread_stale);
    const proofreadStaleAt =
      sourceLang === 'Original'
        ? formatDateTime(selectedChapter.proofread_stale_at)
        : formatDateTime(translatedChapter?.proofread_stale_at);
    const proofreadStaleReason =
      sourceLang === 'Original'
        ? selectedChapter.proofread_stale_reason
        : translatedChapter?.proofread_stale_reason;
    proofreadStaleBadge = isProofreadStale ? (
      <div style={{ marginBottom: 4 }}>
        <Tooltip title={buildStaleTooltip(proofreadStaleReason, proofreadStaleAt || null)}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '2px 8px',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--text-warning, #d97706)',
              background: 'var(--surface-warning, #fff7ed)',
              border: '1px solid var(--border-warning, #f59e0b)',
            }}
          >
            Stale proofread
          </span>
        </Tooltip>
      </div>
    ) : null;

    if (proofreadChangeSetUpdatedAt) {
      const formattedChangeSetUpdatedAt = formatDateTime(proofreadChangeSetUpdatedAt);
      if (formattedChangeSetUpdatedAt) {
        proofreadMetaRows.push({ label: 'Last Proofread Completed', value: formattedChangeSetUpdatedAt });
      }
    } else if (proofreadCompletedOn) {
      proofreadMetaRows.push({ label: 'Last Proofread Completed', value: proofreadCompletedOn });
    }
    proofreadMeta = proofreadMetaRows.map((entry) => `${entry.label}: ${entry.value}`);
  }

  const effectiveProofreadVersion = sourceLang === 'Original' ? 'Original' : `${sourceLang}-${targetLang}`;
  const canLoadProofreadStageTrace = Boolean(
    ADMIN_STAGE_TRACE_UI_ENABLED &&
      isAdmin &&
      apiBase &&
      bookId &&
      chapterGuid &&
      effectiveProofreadVersion,
  );

  useEffect(() => {
    let cancelled = false;
    if (!canLoadProofreadStageTrace) {
      setStageTraceSummary(null);
      setStageTraceChangeAudit(null);
      setStageTraceEvents([]);
      setStageTraceError('');
      setStageTraceLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const fetchStageTrace = async () => {
      try {
        setStageTraceLoading(true);
        setStageTraceError('');
        const headers = await getAuthHeaders();
        const params = new URLSearchParams({
          proofread_version: effectiveProofreadVersion,
          source_lang: sourceLang === 'Original' ? targetLang : sourceLang,
        });
        const response = await fetch(
          `${apiBase}/admin/books/${bookId}/chapters/${chapterGuid}/proofread-stage-traces?${params.toString()}`,
          { headers },
        );
        const payload = (await response.json()) as ProofreadStageTraceResponse & { detail?: string };
        if (!response.ok) {
          throw new Error(payload?.detail || 'Unable to load proofread stage traces.');
        }
        if (cancelled) return;
        setStageTraceSummary((payload?.traceSummary as Record<string, any>) || null);
        setStageTraceChangeAudit((payload?.changeAudit as Record<string, any>) || null);
        setStageTraceEvents(Array.isArray(payload?.events) ? payload.events as Array<Record<string, any>> : []);
      } catch (err: any) {
        if (cancelled) return;
        setStageTraceSummary(null);
        setStageTraceChangeAudit(null);
        setStageTraceEvents([]);
        setStageTraceError(String(err?.message || 'Unable to load proofread stage traces.'));
      } finally {
        if (!cancelled) {
          setStageTraceLoading(false);
        }
      }
    };

    fetchStageTrace();
    return () => {
      cancelled = true;
    };
  }, [apiBase, bookId, chapterGuid, canLoadProofreadStageTrace, effectiveProofreadVersion, sourceLang, targetLang]);

  const proofreadStageCostCoverageSummary = useMemo(() => {
    const unpricedEvents = stageTraceEvents.filter((event) => hasUnpricedTokenUsage(event));
    return {
      unpricedEventCount: unpricedEvents.length,
      unpricedStageNames: Array.from(
        new Set(unpricedEvents.map((event) => String(event.stage || 'unknown_stage'))),
      ),
    };
  }, [stageTraceEvents]);

  const totalProofreadStageCostSummary = useMemo(() => {
    if (!stageTraceEvents.length) return '';
    let inputCost = 0;
    let cachedInputCost = 0;
    let outputCost = 0;
    let totalCost = 0;
    let reasoningOutputCost = 0;
    let nonReasoningOutputCost = 0;

    stageTraceEvents.forEach((event) => {
      const costBreakdown = (event?.cost_breakdown || event?.costBreakdown || {}) as Record<string, any>;
      const costUsd = (costBreakdown?.costUsd || costBreakdown?.cost_usd || {}) as Record<string, any>;
      inputCost += toFiniteNumber(costUsd.input);
      cachedInputCost += toFiniteNumber(costUsd.cachedInput ?? costUsd.cached_input);
      outputCost += toFiniteNumber(costUsd.output);
      totalCost += toFiniteNumber(costUsd.total);
      reasoningOutputCost += toFiniteNumber(costUsd.reasoningOutput ?? costUsd.reasoning_output);
      nonReasoningOutputCost += toFiniteNumber(
        costUsd.nonReasoningOutput ?? costUsd.non_reasoning_output ?? costUsd.outputNonReasoning,
      );
    });

    if (totalCost <= 0 && inputCost <= 0 && outputCost <= 0 && cachedInputCost <= 0) {
      return '';
    }

    const cachedSegment =
      cachedInputCost > 0 ? ` (+ ${formatUsdCompact(cachedInputCost)} cached input)` : '';
    const outputDetailSegment =
      reasoningOutputCost > 0 || nonReasoningOutputCost > 0
        ? ` (${formatUsdCompact(reasoningOutputCost)} reasoning, ${formatUsdCompact(nonReasoningOutputCost)} output)`
        : '';

    return `${formatUsdCompact(inputCost)} input${cachedSegment} - ${formatUsdCompact(outputCost)} output${outputDetailSegment} - ${formatUsdCompact(totalCost)} total`;
  }, [stageTraceEvents]);

  const proofreadMetaTooltipTitle = (
    <div style={{ maxWidth: 380 }}>
      {proofreadMetaRows.length > 0 ? (
        proofreadMetaRows.map((entry) => (
          <div key={`${entry.label}-${entry.value}`}>
            <strong>{entry.label}:</strong> {entry.value}
          </div>
        ))
      ) : (
        <div>Proofread details are unavailable for this chapter.</div>
      )}
    </div>
  );

  const adminCopySections = useMemo((): AdminCopySection[] => {
    const currentSourceText = String(originalText || '').replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
    const currentProofreadText = String(proofreadText || '').replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
    const dialogueSystemPrompt = getFirstTraceTextByStage(stageTraceEvents, ['dialogue'], ['system_prompt', 'systemPrompt']);
    const dialogueUserPrompt = getFirstTraceTextByStage(stageTraceEvents, ['dialogue'], ['user_prompt', 'userPrompt']);
    const dialogueResponses = getAllTraceResponsesByStage(stageTraceEvents, ['dialogue']);
    const formalitySystemPrompt = getFirstTraceTextByStage(stageTraceEvents, ['formality'], ['system_prompt', 'systemPrompt']);
    const formalityUserPrompt = getFirstTraceTextByStage(stageTraceEvents, ['formality'], ['user_prompt', 'userPrompt']);
    const formalityResponses = getAllTraceResponsesByStage(stageTraceEvents, ['formality']);
    const generalSystemPrompt = getFirstTraceTextByStage(stageTraceEvents, ['general'], ['system_prompt', 'systemPrompt']);
    const generalUserPrompt = getFirstTraceTextByStage(stageTraceEvents, ['general'], ['user_prompt', 'userPrompt']);
    const generalResponses = getAllTraceResponsesByStage(stageTraceEvents, ['general']);
    const frictionSystemPrompt = getFirstTraceTextByStage(stageTraceEvents, ['friction'], ['system_prompt', 'systemPrompt']);
    const frictionUserPrompt = getFirstTraceTextByStage(stageTraceEvents, ['friction'], ['user_prompt', 'userPrompt']);
    const frictionResponses = getAllTraceResponsesByStage(stageTraceEvents, ['friction']);
    const changeAuditSystemPrompt = getFirstTraceTextByStage(stageTraceEvents, ['change_audit'], ['system_prompt', 'systemPrompt']);
    const changeAuditUserPrompt = getFirstTraceTextByStage(stageTraceEvents, ['change_audit'], ['user_prompt', 'userPrompt']);
    const changeAuditResponses = getAllTraceResponsesByStage(stageTraceEvents, ['change_audit']);
    const changeAuditPayload = formatAdminTraceValue(stageTraceChangeAudit);

    return [
      {
        key: 'current_source_text',
        label: sourceLang === 'Original' ? 'Original Chapter Text' : 'Current Source Reference Text',
        value: currentSourceText || '(not available)',
        available: Boolean(currentSourceText),
        category: 'base',
      },
      {
        key: 'current_proofread_text',
        label: 'Current Saved Proofread Text',
        value: currentProofreadText || '(not available)',
        available: Boolean(currentProofreadText),
        category: 'base',
      },
      { key: 'dialogue_system_prompt', label: 'Dialogue System Prompt', value: dialogueSystemPrompt, available: Boolean(dialogueSystemPrompt), category: 'rules' },
      { key: 'dialogue_user_prompt', label: 'Dialogue User Prompt', value: dialogueUserPrompt, available: Boolean(dialogueUserPrompt), category: 'rules' },
      { key: 'dialogue_responses', label: 'Dialogue Responses', value: dialogueResponses, available: Boolean(dialogueResponses), category: 'responses' },
      { key: 'formality_system_prompt', label: 'Formality System Prompt', value: formalitySystemPrompt, available: Boolean(formalitySystemPrompt), category: 'rules' },
      { key: 'formality_user_prompt', label: 'Formality User Prompt', value: formalityUserPrompt, available: Boolean(formalityUserPrompt), category: 'rules' },
      { key: 'formality_responses', label: 'Formality Responses', value: formalityResponses, available: Boolean(formalityResponses), category: 'responses' },
      { key: 'general_system_prompt', label: 'General System Prompt', value: generalSystemPrompt, available: Boolean(generalSystemPrompt), category: 'rules' },
      { key: 'general_user_prompt', label: 'General User Prompt', value: generalUserPrompt, available: Boolean(generalUserPrompt), category: 'rules' },
      { key: 'general_responses', label: 'General Responses', value: generalResponses, available: Boolean(generalResponses), category: 'responses' },
      { key: 'friction_system_prompt', label: 'Friction System Prompt', value: frictionSystemPrompt, available: Boolean(frictionSystemPrompt), category: 'rules' },
      { key: 'friction_user_prompt', label: 'Friction User Prompt', value: frictionUserPrompt, available: Boolean(frictionUserPrompt), category: 'rules' },
      { key: 'friction_responses', label: 'Friction Responses', value: frictionResponses, available: Boolean(frictionResponses), category: 'responses' },
      { key: 'change_audit_system_prompt', label: 'Change Audit System Prompt', value: changeAuditSystemPrompt, available: Boolean(changeAuditSystemPrompt), category: 'rules' },
      { key: 'change_audit_user_prompt', label: 'Change Audit User Prompt', value: changeAuditUserPrompt, available: Boolean(changeAuditUserPrompt), category: 'rules' },
      { key: 'change_audit_responses', label: 'Change Audit Responses', value: changeAuditResponses, available: Boolean(changeAuditResponses), category: 'responses' },
      { key: 'change_audit_payload', label: 'Change Audit Payload', value: changeAuditPayload, available: Boolean(changeAuditPayload), category: 'responses' },
    ];
  }, [originalText, proofreadText, sourceLang, stageTraceChangeAudit, stageTraceEvents]);

  const buildAdminTraceCopyPayload = useCallback((selectedKeys: string[]) => {
    const sections = adminCopySections.filter(
      (section) => selectedKeys.includes(section.key) && (section.available || section.value),
    );
    return sections
      .map((section) => `${section.label}\n${ADMIN_COPY_SECTION_DIVIDER}\n${section.value}`)
      .join('\n\n');
  }, [adminCopySections]);

  const openAdminCopyModal = useCallback(() => {
    const defaultKeys = adminCopySections.filter((section) => section.available).map((section) => section.key);
    if (defaultKeys.length === 0) {
      message.warning('No admin trace sections are available to copy.');
      return;
    }
    setSelectedAdminCopyKeys(defaultKeys);
    setIsAdminCopyModalOpen(true);
  }, [adminCopySections]);

  const applyAdminCopyQuickSelection = useCallback((mode: 'all' | 'none' | 'rules' | 'responses') => {
    const availableSections = adminCopySections.filter((section) => section.available);
    if (mode === 'none') {
      setSelectedAdminCopyKeys([]);
      return;
    }
    if (mode === 'all') {
      setSelectedAdminCopyKeys(availableSections.map((section) => section.key));
      return;
    }
    setSelectedAdminCopyKeys(
      availableSections
        .filter((section) => section.category === mode)
        .map((section) => section.key),
    );
  }, [adminCopySections]);

  const copySelectedAdminSections = useCallback(async () => {
    const payload = buildAdminTraceCopyPayload(selectedAdminCopyKeys);
    if (!payload.trim()) {
      message.warning('Select at least one section with content to copy.');
      return;
    }
    await copyTraceSectionToClipboard('Admin trace bundle', payload);
    setIsAdminCopyModalOpen(false);
  }, [buildAdminTraceCopyPayload, copyTraceSectionToClipboard, selectedAdminCopyKeys]);

  const adminProofreadTracePanel = canLoadProofreadStageTrace ? (
    <div
      style={{
        margin: '0 0 12px',
        padding: '10px',
        border: '1px solid var(--border-muted, #d9d9d9)',
        borderRadius: 8,
        background: 'var(--surface-elevated, #fafafa)',
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          marginBottom: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <span>Admin: Proofread Stage Trace</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
            {stageTraceLoading
              ? 'Loading...'
              : `Events: ${stageTraceEvents.length}${stageTraceSummary?.proofread_generation ? ` | Pass ${stageTraceSummary.proofread_generation}` : ''}`}
          </span>
          <Tooltip title="Copy admin trace bundle">
            <Button
              type="default"
              size="small"
              aria-label="Copy admin trace bundle"
              icon={<CopyOutlined />}
              style={topTraceCopyButtonStyle}
              onClick={openAdminCopyModal}
            />
          </Tooltip>
        </div>
      </div>
      {!stageTraceLoading && totalProofreadStageCostSummary && (
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-main)',
            marginBottom: 8,
            fontWeight: 600,
            background: 'var(--surface-muted, rgba(148, 163, 184, 0.16))',
            border: '1px solid var(--border-subtle, rgba(148, 163, 184, 0.35))',
            borderRadius: 6,
            padding: '6px 8px',
          }}
        >
          All-stage cost summary: {totalProofreadStageCostSummary}
        </div>
      )}
      {!stageTraceLoading && proofreadStageCostCoverageSummary.unpricedEventCount > 0 && (
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-warning, #92400e)',
            marginBottom: 8,
            fontWeight: 600,
            background: 'var(--warning-surface, #fff7ed)',
            border: '1px solid var(--warning-border, #fdba74)',
            borderRadius: 6,
            padding: '6px 8px',
          }}
        >
          Cost coverage warning: {proofreadStageCostCoverageSummary.unpricedEventCount} stage event
          {proofreadStageCostCoverageSummary.unpricedEventCount === 1 ? '' : 's'} had token usage but no calculable pricing.
          {proofreadStageCostCoverageSummary.unpricedStageNames.length > 0
            ? ` Missing pricing stages: ${proofreadStageCostCoverageSummary.unpricedStageNames.join(', ')}`
            : ''}
        </div>
      )}
      {stageTraceError && (
        <div style={{ color: 'var(--text-danger, #dc2626)', fontSize: 11, marginBottom: 8 }}>
          {stageTraceError}
        </div>
      )}
      {!stageTraceLoading && stageTraceEvents.length === 0 && !stageTraceError && (
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          No proofread stage trace events found for this chapter.
        </div>
      )}
      {stageTraceEvents.map((event, index) => {
        const eventId = String(event.id || index);
        const stage = String(event.stage || 'unknown_stage');
        const status = String(event.status || '');
        const stageCostSummary = buildStageCostSummary(event);
        const tokenCounts = getEventTokenCounts(event);
        const systemPrompt = formatAdminTraceValue(event.system_prompt ?? event.systemPrompt);
        const userPrompt = formatAdminTraceValue(event.user_prompt ?? event.userPrompt);
        const rawResponse = formatAdminTraceValue(event.raw_response ?? event.rawResponse);
        return (
          <details
            key={eventId}
            style={{
              marginBottom: 8,
              border: '1px solid var(--border-subtle)',
              borderRadius: 6,
              background: 'var(--surface-base)',
              color: 'var(--text-main)',
            }}
          >
            <summary
              style={{
                cursor: 'pointer',
                padding: '8px 10px',
                fontSize: 12,
                fontWeight: 600,
                userSelect: 'text',
                background: 'var(--surface-info)',
                color: 'var(--text-main)',
              }}
            >
              {index + 1}. {stage}
              {status ? ` (${status})` : ''}
              {stageCostSummary ? ` | ${stageCostSummary}` : ''}
              {!stageCostSummary && hasUnpricedTokenUsage(event) ? ' | cost unavailable' : ''}
            </summary>
            <div style={{ padding: '8px 10px', fontSize: 11 }}>
              <div style={{ marginBottom: 8, color: 'var(--text-secondary)' }}>
                Model: {String(event.model_id || event.modelId || '-')}
                {' | '}
                API: {String(event.api_model || event.apiModel || '-')}
              </div>
              <div style={{ marginBottom: 8, color: 'var(--text-secondary)' }}>
                Replacements applied: {String(event.replacements_applied ?? '-')}
                {' | '}
                Replacements failed: {String(event.replacements_failed ?? '-')}
                {' | '}
                Updated chars: {String(event.updated_text_chars ?? '-')}
                {event.alignment_status ? ` | Alignment: ${String(event.alignment_status)}` : ''}
              </div>
              {Object.keys(tokenCounts).length > 0 && (
                <details style={{ marginBottom: 8 }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600, marginBottom: 6 }}>
                    Token Counts
                  </summary>
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      background: 'var(--surface-muted)',
                      color: 'var(--text-main)',
                      padding: 8,
                      borderRadius: 6,
                    }}
                  >
                    {JSON.stringify(tokenCounts, null, 2)}
                  </pre>
                </details>
              )}
              {systemPrompt && (
                <details style={{ marginBottom: 8 }}>
                  <summary
                    style={{
                      cursor: 'pointer',
                      fontWeight: 600,
                      marginBottom: 6,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span>SYSTEM PROMPT</span>
                    <Tooltip title="Copy system prompt">
                      <Button
                        type="default"
                        size="small"
                        aria-label="Copy system prompt"
                        icon={<CopyOutlined />}
                        style={traceCopyButtonStyle}
                        onMouseDown={(copyEvent) => {
                          copyEvent.preventDefault();
                          copyEvent.stopPropagation();
                        }}
                        onClick={(copyEvent) => {
                          copyEvent.preventDefault();
                          copyEvent.stopPropagation();
                          void copyTraceSectionToClipboard('System prompt', systemPrompt);
                        }}
                      />
                    </Tooltip>
                  </summary>
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      background: 'var(--surface-muted)',
                      color: 'var(--text-main)',
                      padding: 8,
                      borderRadius: 6,
                    }}
                  >
                    {systemPrompt}
                  </pre>
                </details>
              )}
              {userPrompt && (
                <details style={{ marginBottom: 8 }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600, marginBottom: 6 }}>
                    USER PROMPT
                  </summary>
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      background: 'var(--surface-muted)',
                      color: 'var(--text-main)',
                      padding: 8,
                      borderRadius: 6,
                    }}
                  >
                    {userPrompt}
                  </pre>
                </details>
              )}
              {rawResponse && (
                <details style={{ marginBottom: 8 }}>
                  <summary
                    style={{
                      cursor: 'pointer',
                      fontWeight: 600,
                      marginBottom: 6,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span>RAW RESPONSE</span>
                    <Tooltip title="Copy raw response">
                      <Button
                        type="default"
                        size="small"
                        aria-label="Copy raw response"
                        icon={<CopyOutlined />}
                        style={traceCopyButtonStyle}
                        onMouseDown={(copyEvent) => {
                          copyEvent.preventDefault();
                          copyEvent.stopPropagation();
                        }}
                        onClick={(copyEvent) => {
                          copyEvent.preventDefault();
                          copyEvent.stopPropagation();
                          void copyTraceSectionToClipboard('Raw response', rawResponse);
                        }}
                      />
                    </Tooltip>
                  </summary>
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      background: 'var(--surface-muted)',
                      color: 'var(--text-main)',
                      padding: 8,
                      borderRadius: 6,
                    }}
                  >
                    {rawResponse}
                  </pre>
                </details>
              )}
              {event.error && (
                <div style={{ marginTop: 8, color: 'var(--text-danger)' }}>
                  Error: {String(event.error)}
                </div>
              )}
            </div>
          </details>
        );
      })}
      <Modal
        open={isAdminCopyModalOpen}
        title="Copy Proofread Trace Bundle"
        onCancel={() => setIsAdminCopyModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsAdminCopyModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="copy"
            type="primary"
            disabled={selectedAdminCopyKeys.length === 0}
            onClick={() => {
              void copySelectedAdminSections();
            }}
          >
            Copy Selected
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button size="small" onClick={() => applyAdminCopyQuickSelection('all')}>All</Button>
          <Button size="small" onClick={() => applyAdminCopyQuickSelection('none')}>None</Button>
          <Button size="small" onClick={() => applyAdminCopyQuickSelection('rules')}>Rules</Button>
          <Button size="small" onClick={() => applyAdminCopyQuickSelection('responses')}>Responses</Button>
        </div>
        <div style={{ display: 'grid', gap: 8, maxHeight: 360, overflowY: 'auto' }}>
          {adminCopySections.map((section) => (
            <Checkbox
              key={section.key}
              checked={selectedAdminCopyKeys.includes(section.key)}
              disabled={!section.available}
              onChange={(event) => {
                const checked = Boolean(event?.target?.checked);
                setSelectedAdminCopyKeys((previous) => {
                  if (checked) {
                    return Array.from(new Set([...previous, section.key]));
                  }
                  return previous.filter((value) => value !== section.key);
                });
              }}
            >
              <span style={{ fontWeight: 600 }}>{section.label}</span>
              {!section.available ? (
                <span style={{ marginLeft: 6, color: 'var(--text-secondary)' }}>(Unavailable)</span>
              ) : null}
            </Checkbox>
          ))}
        </div>
      </Modal>
    </div>
  ) : null;
  const shouldShowProposalReview =
    Boolean(selectedChapter)
    && normalizedProofreadMode === 'proposal'
    && Boolean(proofreadChangeSetId)
    && Boolean(fetchProofreadChangeSet)
    && Boolean(applyProofreadChangeSet)
    && !shouldTreatAsNoChangeProofreadPass;

    useEffect(() => {
      let cancelled = false;
      if (!shouldShowProposalReview || !chapterGuid || !fetchProofreadChangeSet) {
        setProposalChangeSet(null);
        setProposalError('');
        setAcceptedProposalIds(new Set());
        setPreviewProposalIds(new Set());
        setRejectedRegionIds(new Set());
        setAcceptedInitialized(false);
        setReviewPanelOpen(false);
        return () => {
          cancelled = true;
        };
      }
      setProposalLoading(true);
      setProposalError('');
      setPreviewProposalIds(new Set());
      void fetchProofreadChangeSet({
        sourceLang,
        targetLang,
        chapterGuid,
      })
        .then((changeSet) => {
          if (cancelled) return;
          setProposalChangeSet(changeSet || null);
          setAcceptedInitialized(false);
        })
        .catch((error) => {
          if (cancelled) return;
          setProposalError(
            (error as any)?.message || 'Failed to load proofread review changes.',
          );
          setProposalChangeSet(null);
          setAcceptedProposalIds(new Set());
          setRejectedRegionIds(new Set());
          setAcceptedInitialized(false);
          setReviewPanelOpen(false);
        })
        .finally(() => {
          if (cancelled) return;
          setProposalLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }, [
      applyProofreadChangeSet,
      chapterGuid,
      fetchProofreadChangeSet,
      shouldShowProposalReview,
      sourceLang,
      targetLang,
      proofreadChangeSetId,
      proposalReloadNonce,
    ]);

    const allProposalRows = useMemo(
      () => sortProposals((proposalChangeSet?.proposals || []) as ProofreadProposal[]),
      [proposalChangeSet],
    );
    const reviewableProposalRows = useMemo(
      () => allProposalRows.filter((proposal) => proposal.reviewable),
      [allProposalRows],
    );
    const { groups: conflictGroups, proposalToGroup } = useMemo(
      () => buildConflictGroups(reviewableProposalRows),
      [reviewableProposalRows],
    );
    const reviewSurface = useMemo(
      () => buildReviewRegions(String(proposalChangeSet?.base_text || originalText || ''), conflictGroups),
      [conflictGroups, originalText, proposalChangeSet],
    );
    const reviewRegions = reviewSurface.regions;
    const reviewableRegionCount = reviewRegions.length;
    const visibleReviewGroupIds = useMemo(
      () => new Set(reviewRegions.map((region) => region.groupId)),
      [reviewRegions],
    );
    const unmappedConflictGroups = useMemo(
      () => conflictGroups.filter((group) => !visibleReviewGroupIds.has(group.groupId)),
      [conflictGroups, visibleReviewGroupIds],
    );
    const unmappedReviewableProposalCount = useMemo(
      () => unmappedConflictGroups.reduce((total, group) => total + group.proposals.length, 0),
      [unmappedConflictGroups],
    );
    const hasUnmappedReviewableChanges = unmappedConflictGroups.length > 0;
    const totalReviewSuggestionCount = reviewableRegionCount + unmappedConflictGroups.length;
    const unmappedProposalDiagnostics = useMemo(() => {
      if (!ADMIN_STAGE_TRACE_UI_ENABLED || !hasUnmappedReviewableChanges) {
        return [];
      }
      const effectiveBaseText = String(proposalChangeSet?.base_text || originalText || '');
      const effectivePlainText = reviewSurface.plainText || '';
      return unmappedConflictGroups.flatMap((group) =>
        group.proposals.map((proposal) => {
          const locatedStart = Number(proposal.located_start);
          const locatedEnd = Number(proposal.located_end);
          const normalizedSource = normalizeText(htmlToDiffText(String(proposal.source_text || '')));
          const baseSlice =
            Number.isFinite(locatedStart) && Number.isFinite(locatedEnd)
              ? String(effectiveBaseText || '').slice(
                  Math.max(0, Math.floor(locatedStart)),
                  Math.max(Math.floor(locatedStart), Math.floor(locatedEnd)),
                )
              : '';
          const normalizedBaseSlice = normalizeText(htmlToDiffText(baseSlice));
          const derivedPlainStart =
            Number.isFinite(locatedStart)
              ? normalizeText(
                  htmlToDiffText(String(effectiveBaseText || '').slice(0, Math.max(0, Math.floor(locatedStart)))),
                ).length
              : null;
          const derivedPlainEnd =
            derivedPlainStart !== null ? derivedPlainStart + normalizedBaseSlice.length : null;
          const plainSlice =
            derivedPlainStart !== null && derivedPlainEnd !== null
              ? String(effectivePlainText || '').slice(
                  Math.max(0, derivedPlainStart),
                  Math.max(Math.max(0, derivedPlainStart), derivedPlainEnd),
                )
              : '';
          return {
            proposalId: String(proposal.proposal_id || ''),
            groupId: group.groupId,
            sourceLength: normalizedSource.length,
            locatedStart: Number.isFinite(locatedStart) ? Math.floor(locatedStart) : null,
            locatedEnd: Number.isFinite(locatedEnd) ? Math.floor(locatedEnd) : null,
            derivedPlainStart,
            derivedPlainEnd,
            sourceText: String(proposal.source_text || ''),
            normalizedSource,
            baseSlice,
            normalizedBaseSlice,
            plainSlice,
            baseTextLength: effectiveBaseText.length,
            plainTextLength: effectivePlainText.length,
          };
        }),
      );
    }, [
      hasUnmappedReviewableChanges,
      originalText,
      proposalChangeSet,
      reviewSurface.plainText,
      unmappedConflictGroups,
    ]);

    useEffect(() => {
      if (!shouldShowProposalReview || acceptedInitialized) return;
      if (!reviewableProposalRows.length) {
        setAcceptedProposalIds(new Set());
        setRejectedRegionIds(new Set());
        setAcceptedInitialized(true);
        return;
      }
      const preApplied = (proposalChangeSet?.accepted_proposal_ids || []).filter(Boolean);
      const baselineIds = preApplied.length ? new Set(preApplied) : new Set<string>();
      setAcceptedProposalIds(resolveAcceptedProposalIds(reviewableProposalRows, baselineIds));
      setRejectedRegionIds(new Set());
      setAcceptedInitialized(true);
    }, [
      acceptedInitialized,
      proposalChangeSet,
      reviewableProposalRows,
      shouldShowProposalReview,
    ]);

    const baselineAcceptedProposalIds = useMemo(() => {
      if (!reviewableProposalRows.length) return new Set<string>();
      const preApplied = (proposalChangeSet?.accepted_proposal_ids || []).filter(Boolean);
      const baselineIds = preApplied.length ? new Set(preApplied) : new Set<string>();
      return resolveAcceptedProposalIds(reviewableProposalRows, baselineIds);
    }, [proposalChangeSet, reviewableProposalRows]);

    useEffect(() => {
      if (!shouldShowProposalReview) {
        setSelectedRegionId('');
        setPreviewProposalIds(new Set());
        setReviewPanelOpen(false);
        return;
      }
      if (!reviewRegions.length) {
        setSelectedRegionId('');
        setReviewPanelOpen(false);
        return;
      }
      if (reviewRegions.some((region) => region.regionId === selectedRegionId)) {
        return;
      }
      if (selectedRegionId) {
        setSelectedRegionId('');
      }
    }, [reviewRegions, selectedRegionId, shouldShowProposalReview]);

    useEffect(() => {
      if (!selectedRegionId) return;
      const element = regionElementRefs.current[selectedRegionId];
      if (!element || typeof element.scrollIntoView !== 'function') return;
      element.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
    }, [selectedRegionId]);

    const resolvedAcceptedProposalIds = useMemo(
      () => resolveAcceptedProposalIds(reviewableProposalRows, acceptedProposalIds),
      [reviewableProposalRows, acceptedProposalIds],
    );
    const resolvedPreviewProposalIds = useMemo(
      () => resolveAcceptedProposalIds(reviewableProposalRows, previewProposalIds),
      [reviewableProposalRows, previewProposalIds],
    );
    const hasUnsavedReviewDecisions = useMemo(
      () => !areStringSetsEqual(resolvedAcceptedProposalIds, baselineAcceptedProposalIds),
      [baselineAcceptedProposalIds, resolvedAcceptedProposalIds],
    );
    const reviewDraftKey = useMemo(() => {
      const runId = String(proposalChangeSet?.run_id || '').trim();
      if (!runId || !chapterGuid) return '';
      return `proofread-review-draft:${runId}:${chapterGuid}:${sourceLang}:${targetLang}`;
    }, [chapterGuid, proposalChangeSet?.run_id, sourceLang, targetLang]);

    useEffect(() => {
      if (!shouldShowProposalReview || !acceptedInitialized || !reviewDraftKey) {
        if (restoredDraftKey) {
          setRestoredDraftKey('');
        }
        return;
      }
      if (restoredDraftKey === reviewDraftKey) {
        return;
      }
      try {
        const rawDraft = window.localStorage.getItem(reviewDraftKey);
        if (rawDraft) {
          const draft = JSON.parse(rawDraft) as {
            acceptedProposalIds?: string[];
            previewProposalIds?: string[];
            rejectedRegionIds?: string[];
          };
          setAcceptedProposalIds(resolveAcceptedProposalIds(
            reviewableProposalRows,
            new Set(draft.acceptedProposalIds || []),
          ));
          setPreviewProposalIds(resolveAcceptedProposalIds(
            reviewableProposalRows,
            new Set(draft.previewProposalIds || []),
          ));
          setRejectedRegionIds(new Set(
            (draft.rejectedRegionIds || []).filter(regionId =>
              reviewRegions.some(region => region.regionId === regionId),
            ),
          ));
          message.info?.(
            'Recovered unsaved Accept/Reject selections on this device. Saved chapter text was not changed.',
          );
          recordUxEvent('proofread_review_draft_restored');
        }
      } catch (_error) {
        window.localStorage.removeItem(reviewDraftKey);
      } finally {
        setRestoredDraftKey(reviewDraftKey);
      }
    }, [
      acceptedInitialized,
      reviewDraftKey,
      reviewRegions,
      reviewableProposalRows,
      restoredDraftKey,
      shouldShowProposalReview,
    ]);

    useEffect(() => {
      if (!reviewDraftKey || restoredDraftKey !== reviewDraftKey) return;
      try {
        if (!hasUnsavedReviewDecisions && rejectedRegionIds.size === 0 && previewProposalIds.size === 0) {
          window.localStorage.removeItem(reviewDraftKey);
          return;
        }
        window.localStorage.setItem(reviewDraftKey, JSON.stringify({
          acceptedProposalIds: Array.from(resolvedAcceptedProposalIds),
          previewProposalIds: Array.from(resolvedPreviewProposalIds),
          rejectedRegionIds: Array.from(rejectedRegionIds),
          updatedAt: new Date().toISOString(),
        }));
      } catch (_error) {
        // Draft recovery is best-effort and must never block review decisions.
      }
    }, [
      hasUnsavedReviewDecisions,
      previewProposalIds.size,
      rejectedRegionIds,
      resolvedAcceptedProposalIds,
      resolvedPreviewProposalIds,
      restoredDraftKey,
      reviewDraftKey,
    ]);
    const acceptedRegionCount = useMemo(
      () => reviewRegions.reduce((total, region) => {
        const preferredId = getPreferredProposalIdForRegion(region, resolvedAcceptedProposalIds);
        return total + (preferredId && resolvedAcceptedProposalIds.has(preferredId) ? 1 : 0);
      }, 0),
      [resolvedAcceptedProposalIds, reviewRegions],
    );
    const selectedRegionIndex = selectedRegionId
      ? reviewRegions.findIndex((region) => region.regionId === selectedRegionId)
      : reviewPanelOpen && reviewRegions.length
        ? 0
        : -1;
    const selectedRegion = selectedRegionId
      ? reviewRegions.find((region) => region.regionId === selectedRegionId) || null
      : reviewPanelOpen && reviewRegions.length
        ? reviewRegions[0]
        : null;
    const selectedProposalId = selectedRegion
      ? getPreferredProposalIdForRegion(selectedRegion, resolvedAcceptedProposalIds)
      : '';
    const selectedProposal = selectedRegion?.proposals.find(
      (proposal) => String(proposal.proposal_id || '') === selectedProposalId,
    ) || null;

    useEffect(() => {
      const runId = String(proposalChangeSet?.run_id || '');
      if (!selectedRegion || !runId || trackedReviewOpenRef.current === runId) return;
      trackedReviewOpenRef.current = runId;
      recordUxEvent('proofread_review_opened', {
        suggestion_count: totalReviewSuggestionCount,
        has_unmapped_changes: hasUnmappedReviewableChanges,
      });
    }, [hasUnmappedReviewableChanges, proposalChangeSet?.run_id, selectedRegion, totalReviewSuggestionCount]);

    const dumpSelectedReviewHtml = useCallback(() => {
      if (!selectedRegion || !selectedProposal) {
        console.log('[ProofreadReviewHTML]', { error: 'no_selected_change' });
        return;
      }
      const baseHtml = String(proposalChangeSet?.base_text || originalText || '');
      const sourceHtml = String(selectedProposal.source_text || '');
      const targetHtml = String(selectedProposal.target_text || '');
      const baseIndex = sourceHtml ? baseHtml.indexOf(sourceHtml) : -1;
      const contextStart = baseIndex >= 0 ? Math.max(0, baseIndex - 240) : 0;
      const contextEnd = baseIndex >= 0 ? Math.min(baseHtml.length, baseIndex + sourceHtml.length + 240) : Math.min(baseHtml.length, 480);
      console.log('[ProofreadReviewHTML]', {
        chapterGuid,
        proofreadVersion: sourceLang === 'Original' ? 'Original' : `${sourceLang}-${targetLang}`,
        regionId: selectedRegion.regionId,
        proposalId: String(selectedProposal.proposal_id || ''),
        baseIndex,
        sourceHtml,
        targetHtml,
        baseHtmlContext: baseHtml.slice(contextStart, contextEnd),
        fullBaseHtml: baseHtml,
      });
    }, [
      chapterGuid,
      originalText,
      proposalChangeSet,
      selectedProposal,
      selectedRegion,
      sourceLang,
      targetLang,
    ]);

    useEffect(() => {
      if (!debugProofreadReview) return;
      dumpSelectedReviewHtml();
    }, [debugProofreadReview, dumpSelectedReviewHtml]);

    useEffect(() => {
      if (typeof window === 'undefined') return undefined;
      window.__dumpProofreadReviewHtml = dumpSelectedReviewHtml;
      return () => {
        if (window.__dumpProofreadReviewHtml === dumpSelectedReviewHtml) {
          delete window.__dumpProofreadReviewHtml;
        }
      };
    }, [dumpSelectedReviewHtml]);

    const toggleProposalAccepted = (proposalId: string, checked: boolean) => {
      const proposalIdTrimmed = String(proposalId || '').trim();
      if (!proposalIdTrimmed) return;
      const groupId = proposalToGroup.get(proposalIdTrimmed);
      const group = conflictGroups.find((entry) => entry.groupId === groupId);
      setAcceptedProposalIds((previous) => {
        const next = new Set(previous);
        if (group && group.proposals.length > 1) {
          group.proposals.forEach((proposal) => next.delete(proposal.proposal_id));
          if (checked) {
            next.add(proposalIdTrimmed);
          }
          return resolveAcceptedProposalIds(reviewableProposalRows, next);
        }
        if (checked) {
          next.add(proposalIdTrimmed);
        } else {
          next.delete(proposalIdTrimmed);
        }
        return resolveAcceptedProposalIds(reviewableProposalRows, next);
      });
    };

    const toggleProposalPreview = (proposalId: string, checked: boolean) => {
      const proposalIdTrimmed = String(proposalId || '').trim();
      if (!proposalIdTrimmed) return;
      const groupId = proposalToGroup.get(proposalIdTrimmed);
      const group = conflictGroups.find((entry) => entry.groupId === groupId);
      setPreviewProposalIds((previous) => {
        const next = new Set(previous);
        if (group) {
          group.proposals.forEach((proposal) => next.delete(String(proposal.proposal_id || '')));
        }
        if (checked) {
          next.add(proposalIdTrimmed);
        }
        return resolveAcceptedProposalIds(reviewableProposalRows, next);
      });
    };

    const advanceFromRegion = (regionId: string) => {
      const currentIndex = reviewRegions.findIndex((region) => region.regionId === regionId);
      if (currentIndex < 0) return;
      if (currentIndex < reviewRegions.length - 1) {
        setSelectedRegionId(reviewRegions[currentIndex + 1].regionId);
      }
    };

    const acceptRegionProposal = (region: ReviewRegion, proposalId: string) => {
      toggleProposalAccepted(proposalId, true);
      setRejectedRegionIds((previous) => {
        const next = new Set(previous);
        next.delete(region.regionId);
        return next;
      });
      setPreviewProposalIds((previous) => {
        const next = new Set(previous);
        region.proposals.forEach((proposal) => next.delete(String(proposal.proposal_id || '')));
        next.add(String(proposalId || '').trim());
        return next;
      });
      advanceFromRegion(region.regionId);
    };

    const rejectRegion = (region: ReviewRegion) => {
      setAcceptedProposalIds((previous) => {
        const next = new Set(previous);
        region.proposals.forEach((proposal) => {
          next.delete(String(proposal.proposal_id || ''));
        });
        return resolveAcceptedProposalIds(reviewableProposalRows, next);
      });
      setPreviewProposalIds((previous) => {
        const next = new Set(previous);
        region.proposals.forEach((proposal) => next.delete(String(proposal.proposal_id || '')));
        return next;
      });
      setRejectedRegionIds((previous) => {
        const next = new Set(previous);
        next.add(region.regionId);
        return next;
      });
      advanceFromRegion(region.regionId);
    };

    const goToAdjacentRegion = (direction: -1 | 1) => {
      if (!reviewRegions.length) return;
      const currentIndex = selectedRegionIndex >= 0 ? selectedRegionIndex : 0;
      const nextIndex = currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= reviewRegions.length) return;
      setReviewPanelOpen(true);
      setSelectedRegionId(reviewRegions[nextIndex].regionId);
    };

    const acceptAllRegions = () => {
      const next = new Set<string>();
      reviewRegions.forEach((region) => {
        const preferredId = getPreferredProposalIdForRegion(region, resolvedAcceptedProposalIds);
        if (preferredId) {
          next.add(preferredId);
        }
      });
      setAcceptedProposalIds(resolveAcceptedProposalIds(reviewableProposalRows, next));
      setPreviewProposalIds(resolveAcceptedProposalIds(reviewableProposalRows, next));
      setRejectedRegionIds(new Set());
    };

    const handleApplyAcceptedChanges = async () => {
      if (!applyProofreadChangeSet || !proposalChangeSet?.run_id) {
        return;
      }
      setIsSaveAcceptedConfirmationOpen(false);
      setIsApplyingProposals(true);
      try {
        await applyProofreadChangeSet({
          sourceLang,
          targetLang,
          chapterGuid,
          changeSetId: proposalChangeSet.run_id,
          acceptedProposalIds: Array.from(resolvedAcceptedProposalIds),
        });
        message.success('Applied accepted proofreading changes.');
        recordUxEvent('proofread_review_saved', {
          accepted_count: acceptedRegionCount,
          not_accepted_count: unacceptedRegionCount,
        });
        if (reviewDraftKey) {
          window.localStorage.removeItem(reviewDraftKey);
        }
        setPreviewProposalIds(new Set());
        setRejectedRegionIds(new Set());
        setSelectedRegionId('');
        setReviewPanelOpen(false);
        if (onRefreshProofreadContent) {
          await onRefreshProofreadContent();
        }
      } catch (error) {
        const detail = String((error as any)?.message || '').trim();
        const normalizedDetail = detail.toLowerCase();
        if (
          normalizedDetail.includes('no longer current')
          || normalizedDetail.includes('latest proofread')
          || normalizedDetail.includes('removed from pending review')
          || normalizedDetail.includes('re-run proofreading')
          || normalizedDetail.includes('glossary rules changed')
          || normalizedDetail.includes('changed during review')
        ) {
          message.warning(detail, 10);
        } else {
          message.error(detail || 'Failed to apply accepted proofreading changes.');
        }
      } finally {
        setIsApplyingProposals(false);
      }
    };

    const unacceptedRegionCount = Math.max(
      reviewableRegionCount - acceptedRegionCount + unmappedConflictGroups.length,
      0,
    );

    const handleSaveAcceptedChangesClick = () => {
      if (unacceptedRegionCount > 0) {
        setIsSaveAcceptedConfirmationOpen(true);
        return;
      }
      void handleApplyAcceptedChanges();
    };

    const handleDiscardProposalReview = async () => {
      if (!discardProofreadChangeSet || !proposalChangeSet?.run_id) {
        return;
      }
      const confirmed = window.confirm(
        'Discard this proofread review? None of these proposed changes will be applied.',
      );
      if (!confirmed) {
        return;
      }
      setIsDiscardingProposalReview(true);
      try {
        await discardProofreadChangeSet({
          sourceLang,
          targetLang,
          chapterGuid,
          changeSetId: proposalChangeSet.run_id,
        });
        message.success('Discarded proofread review.');
        if (reviewDraftKey) {
          window.localStorage.removeItem(reviewDraftKey);
        }
        setProposalChangeSet(null);
        setAcceptedProposalIds(new Set());
        setPreviewProposalIds(new Set());
        setRejectedRegionIds(new Set());
        setSelectedRegionId('');
        setReviewPanelOpen(false);
        if (onRefreshProofreadContent) {
          await onRefreshProofreadContent();
        }
      } catch (error) {
        const detail = String((error as any)?.message || '').trim();
        if (detail.toLowerCase().includes('no longer current') || detail.toLowerCase().includes('latest proofread')) {
          message.warning(detail, 10);
        } else {
          message.error(detail || 'Failed to discard proofread review.');
        }
      } finally {
        setIsDiscardingProposalReview(false);
      }
    };

    useEffect(() => {
      const beforeUnload = (event: BeforeUnloadEvent) => {
        if (!shouldShowProposalReview || !hasUnsavedReviewDecisions) return;
        event.preventDefault();
        event.returnValue = '';
      };
      window.addEventListener('beforeunload', beforeUnload);
      return () => window.removeEventListener('beforeunload', beforeUnload);
    }, [hasUnsavedReviewDecisions, shouldShowProposalReview]);

    const renderInteractiveChapterText = () => {
      const nodes: React.ReactNode[] = [];
      const plainText = reviewSurface.plainText || '';
      let cursor = 0;
      reviewRegions.forEach((region) => {
        if (region.start > cursor) {
          nodes.push(
            <span key={`plain-${cursor}`}>
              {plainText.slice(cursor, region.start)}
            </span>,
          );
        }
        const proposalId = getPreferredProposalIdForRegion(region, resolvedAcceptedProposalIds);
        const isAccepted = Boolean(proposalId) && resolvedAcceptedProposalIds.has(proposalId);
        const isSelected = region.regionId === selectedRegionId;
        const acceptedProposal = isAccepted
          ? region.proposals.find((proposal) => String(proposal.proposal_id || '') === proposalId) || null
          : null;
        const previewedProposal =
          region.proposals.find((proposal) => resolvedPreviewProposalIds.has(String(proposal.proposal_id || ''))) || null;
        const displayProposal = previewedProposal || null;
        const displayText = displayProposal
          ? formatReviewDisplayText(String(displayProposal.target_text || ''))
          : String(plainText.slice(region.start, region.end) || region.sourceText || '');
        const regionVisualState: ReviewRegionVisualState = isAccepted
          ? 'accepted'
          : previewedProposal
            ? 'preview'
            : rejectedRegionIds.has(region.regionId)
              ? 'rejected'
              : 'pending';
        const palette = getRegionVisualPalette(token as Record<string, any>, regionVisualState, isSelected);
        if (region.hasStructuralHtml) {
          const structuralHtml = displayProposal
            ? String(displayProposal.target_text || '')
            : String(region.proposals[0]?.source_text || '');
          nodes.push(
            <div
              key={region.regionId}
              ref={(node) => {
                regionElementRefs.current[region.regionId] = node;
              }}
              onClick={() => {
                setReviewPanelOpen(true);
                setSelectedRegionId(region.regionId);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setReviewPanelOpen(true);
                  setSelectedRegionId(region.regionId);
                }
              }}
              role="button"
              tabIndex={0}
              data-testid={`proposal-region-${region.regionId}`}
              data-review-state={regionVisualState}
              style={{
                display: 'block',
                border: `1px solid ${palette.border}`,
                background: palette.background,
                color: palette.text,
                cursor: 'pointer',
                borderRadius: 8,
                padding: '8px 10px',
                margin: '8px 0',
                boxShadow: palette.shadow,
                transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
              }}
            >
              <div
                style={{
                  color: palette.text,
                }}
                dangerouslySetInnerHTML={renderReviewHtml(structuralHtml)}
              />
            </div>,
          );
        } else {
          nodes.push(
            <button
              key={region.regionId}
              type="button"
              ref={(node) => {
                regionElementRefs.current[region.regionId] = node;
              }}
              onClick={() => {
                setReviewPanelOpen(true);
                setSelectedRegionId(region.regionId);
              }}
              data-testid={`proposal-region-${region.regionId}`}
              data-review-state={regionVisualState}
              style={{
                border: `1px solid ${palette.border}`,
                background: palette.background,
                color: palette.text,
                cursor: 'pointer',
                borderRadius: 4,
                padding: '1px 3px',
                margin: '0 1px',
                display: 'inline',
                verticalAlign: 'baseline',
                font: 'inherit',
                lineHeight: 'inherit',
                boxShadow: palette.shadow,
                transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
              }}
            >
              {displayText}
            </button>,
          );
        }
        cursor = region.end;
      });
      if (cursor < plainText.length) {
        nodes.push(
          <span key={`plain-tail-${cursor}`}>
            {plainText.slice(cursor)}
          </span>,
        );
      }
      return nodes;
    };

  if (shouldShowProposalReview) {
      const changeSummary = proposalChangeSet?.summary || {};
      const invalidProposalRows = allProposalRows.filter((proposal) => !proposal.reviewable);
      proofreadContent = (
        <div>
          {adminProofreadTracePanel}
          <div
            className="proofread-review-toolbar"
            data-testid="tutorial:proofread-review:header"
          >
            <div>
              <h3 style={{ marginBottom: 2 }}>Proofread Review</h3>
              <div style={{ fontSize: 12, color: token.colorTextSecondary || 'var(--text-secondary)' }}>
                Review highlighted text in place. Decisions are not saved until you click Save Accepted Changes.
              </div>
              <a className="proofread-context-help" href="/support?tab=articles&article=proofread-a-chapter">
                <QuestionCircleOutlined /> How proofreading works
              </a>
              {proofreadGeneration ? (
                <div style={{ fontSize: 12, color: token.colorTextSecondary || 'var(--text-secondary)', marginTop: 4 }}>
                  {`Proofread Pass: ${proofreadGeneration}`}
                </div>
              ) : null}
              {proofreadModel ? (
                <div style={{ fontSize: 12, color: token.colorTextSecondary || 'var(--text-secondary)', marginTop: 2 }}>
                  {`Model: ${proofreadModel}`}
                </div>
              ) : null}
            </div>
            <div className="proofread-review-toolbar__actions">
              <div
                data-testid="tutorial:proofread-review:actions"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                }}
              >
                <span data-testid="tutorial:proofread-review:open-panel" style={{ display: 'inline-flex' }}>
                  <Button
                    onClick={() => {
                      if (!reviewRegions.length) return;
                      setReviewPanelOpen(true);
                      const nextRegionId =
                        (selectedRegionId && reviewRegions.some((region) => region.regionId === selectedRegionId))
                          ? selectedRegionId
                          : reviewRegions[0].regionId;
                      setSelectedRegionId(nextRegionId);
                    }}
                    data-testid="open-review-panel"
                    disabled={proposalLoading || Boolean(proposalError) || reviewRegions.length === 0}
                  >
                    Open Review
                  </Button>
                </span>
                <span data-testid="tutorial:proofread-review:accept-all" style={{ display: 'inline-flex' }}>
                  <Button
                    onClick={acceptAllRegions}
                    data-testid="accept-all-proposals"
                    disabled={proposalLoading || Boolean(proposalError) || isApplyingProposals || reviewRegions.length === 0}
                  >
                    Accept All
                  </Button>
                </span>
                <span data-testid="tutorial:proofread-review:reject-all" style={{ display: 'inline-flex' }}>
                  <Button
                    onClick={() => {
                      setAcceptedProposalIds(new Set());
                      setRejectedRegionIds(new Set(reviewRegions.map((region) => region.regionId)));
                    }}
                    data-testid="reject-all-proposals"
                    disabled={proposalLoading || isApplyingProposals || isDiscardingProposalReview}
                  >
                    Reject All
                  </Button>
                </span>
                {discardProofreadChangeSet ? (
                  <span data-testid="tutorial:proofread-review:discard" style={{ display: 'inline-flex' }}>
                    <Button
                      danger
                      onClick={() => {
                        void handleDiscardProposalReview();
                      }}
                      data-testid="discard-proofread-review"
                      disabled={proposalLoading || isApplyingProposals || isDiscardingProposalReview}
                    >
                      {isDiscardingProposalReview ? 'Discarding…' : 'Discard Review'}
                    </Button>
                  </span>
                ) : null}
              </div>
              <div
                className="proofread-review-summary-pill"
                data-testid="tutorial:proofread-review:summary"
                style={{
                  fontSize: 12,
                  color: token.colorTextSecondary || 'var(--text-secondary)',
                  padding: '6px 10px',
                  borderRadius: 999,
                  border: `1px solid ${token.colorBorderSecondary || 'var(--border-subtle)'}`,
                  background: token.colorFillAlter || 'rgba(148, 163, 184, 0.10)',
                }}
              >
                {`Suggestions ${totalReviewSuggestionCount} | Accepted ${acceptedRegionCount} | Not Accepted ${unacceptedRegionCount}`}
              </div>
            </div>
          </div>
          {proposalLoading && (
            <div style={{ marginBottom: 12 }}>Loading proofread change proposals…</div>
          )}
          {!proposalLoading && proposalError && (
            <div className="proofread-review-load-failure" role="alert">
              <div>
                <strong>Proofread review could not be displayed.</strong>
                <p>{proposalError}</p>
              </div>
              <div className="proofread-review-load-failure__actions">
                <Button onClick={() => setProposalReloadNonce(value => value + 1)}>Retry review</Button>
                <a href="/support?tab=articles&article=proofread-a-chapter">Get help</a>
              </div>
            </div>
          )}
          {!proposalLoading && !proposalError && hasUnmappedReviewableChanges && (
            <>
              <div
                className="proofread-review-warning"
                style={{
                  marginBottom: 12,
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: `1px solid ${token.colorWarningBorder || '#d97706'}`,
                  background: token.colorWarningBg || 'rgba(245, 158, 11, 0.12)',
                  color: token.colorText || 'var(--text-main)',
                }}
              >
                {`${unmappedReviewableProposalCount} proposed change${unmappedReviewableProposalCount === 1 ? '' : 's'} could not be matched to the saved chapter text, so ${unmappedReviewableProposalCount === 1 ? 'it will' : 'they will'} be treated as not accepted when you save. You can still save your other review decisions without rerunning proofreading.`}
              </div>
              {ADMIN_STAGE_TRACE_UI_ENABLED && unmappedProposalDiagnostics.length > 0 ? (
                <details
                  className="proofread-review-diagnostics"
                  style={{
                    marginBottom: 12,
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: `1px solid ${token.colorBorderSecondary || 'var(--border-subtle)'}`,
                    background: token.colorFillAlter || 'rgba(148, 163, 184, 0.10)',
                    color: token.colorText || 'var(--text-main)',
                  }}
                >
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                    Unmapped proposal diagnostics
                  </summary>
                  <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.55 }}>
                    {unmappedProposalDiagnostics.map((row) => (
                      <div key={`${row.groupId}-${row.proposalId}`} style={{ marginBottom: 12 }}>
                        <div><strong>{row.proposalId}</strong> ({row.groupId})</div>
                        <div>{`located: ${row.locatedStart ?? 'n/a'}..${row.locatedEnd ?? 'n/a'} | derived plain: ${row.derivedPlainStart ?? 'n/a'}..${row.derivedPlainEnd ?? 'n/a'} | base len: ${row.baseTextLength} | plain len: ${row.plainTextLength}`}</div>
                        <div>{`normalized source (${row.sourceLength}): ${row.normalizedSource || '[empty]'}`}</div>
                        <div>{`normalized base slice: ${row.normalizedBaseSlice || '[empty]'}`}</div>
                        <div>{`plain slice: ${row.plainSlice || '[empty]'}`}</div>
                      </div>
                    ))}
                  </div>
                </details>
              ) : null}
            </>
          )}
          {!proposalLoading && !proposalError && reviewRegions.length === 0 && (
            <div className="proofread-review-load-failure" role="status">
              <div>
                <strong>No highlighted changes could be displayed.</strong>
                <p>
                  {hasUnmappedReviewableChanges
                    ? 'No visible review regions could be built from the current text. The saved suggestions no longer align with the visible chapter text; you can still save them as not accepted or retry loading the review.'
                    : 'No reviewable change regions were found.'}
                </p>
              </div>
              <div className="proofread-review-load-failure__actions">
                <Button onClick={() => setProposalReloadNonce(value => value + 1)}>Retry review</Button>
                <a href="/support?tab=articles&article=proofread-a-chapter">Get help</a>
              </div>
            </div>
          )}
          {!proposalLoading && !proposalError && reviewRegions.length > 0 && (
            <div
              className={`proofread-review-workspace${selectedRegion ? ' proofread-review-workspace--panel-open' : ''}`}
              data-testid="tutorial:proofread-review:workspace"
            >
              <div data-testid="tutorial:proofread-review:text">
                <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                  Click a highlighted span to review that change.
                </div>
                <div
                  className="proofread-review-text-surface"
                  data-testid="proofread-review-text-surface"
                  style={{
                    ...contentStyle,
                    marginRight: 0,
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.7,
                    height: 'clamp(350px, 48vh, 470px)',
                    minHeight: 240,
                    maxHeight: 'clamp(350px, 48vh, 470px)',
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    overflowY: 'auto',
                  }}
                >
                  {renderInteractiveChapterText()}
                </div>
              </div>
              {selectedRegion && (
              <div
                className="proofread-review-popover"
                data-testid="proofread-review-popover"
                style={{ borderColor: token.colorBorderSecondary || 'var(--border-subtle)' }}
              >
                <div data-testid="tutorial:proofread-review:popover">
                <div
                  data-testid="proofread-review-popover-drag-handle"
                  className="proofread-review-panel-header"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '10px 12px',
                    borderBottom: `1px solid ${token.colorBorderSecondary || 'var(--border-subtle)'}`,
                    background: token.colorFillAlter || 'rgba(148, 163, 184, 0.10)',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <strong style={{ fontSize: 13 }}>Review change</strong>
                    <span style={{ fontSize: 11, color: token.colorTextSecondary || 'var(--text-secondary)' }}>
                      {selectedRegionIndex >= 0 ? `Change ${selectedRegionIndex + 1} of ${reviewRegions.length}` : 'Select a highlighted change.'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Button
                      size="small"
                      data-testid="proofread-review-prev"
                      disabled={selectedRegionIndex <= 0}
                      onClick={() => goToAdjacentRegion(-1)}
                    >
                      Previous
                    </Button>
                    <Button
                      size="small"
                      data-testid="proofread-review-next"
                      disabled={selectedRegionIndex < 0 || selectedRegionIndex >= reviewRegions.length - 1}
                      onClick={() => goToAdjacentRegion(1)}
                    >
                      Next
                    </Button>
                    <span data-testid="tutorial:proofread-review:hide-panel" style={{ display: 'inline-flex' }}>
                      <Button size="small" onClick={() => {
                        setReviewPanelOpen(false);
                        setSelectedRegionId('');
                      }}>
                        Hide Panel
                      </Button>
                    </span>
                  </div>
                </div>
                <div style={{ padding: 12, maxHeight: 500, overflowY: 'auto' }}>
                  <div>
                    <div style={{ marginBottom: 10 }}>
                      <strong>Current text</strong>
                      <div
                        style={{
                          marginTop: 4,
                          padding: 10,
                          borderRadius: 8,
                          background: token.colorFillTertiary || 'rgba(148, 163, 184, 0.10)',
                          border: `1px solid ${token.colorBorderSecondary || 'var(--border-subtle)'}`,
                        }}
                      >
                        {selectedRegion.hasStructuralHtml ? (
                          <div dangerouslySetInnerHTML={renderReviewHtml(String(selectedRegion.proposals[0]?.source_text || ''))} />
                        ) : (
                          formatReviewDisplayText(selectedRegion.sourceText)
                        )}
                      </div>
                    </div>
                    {selectedRegion.proposals.length > 1 && (
                      <div style={{ marginBottom: 10, fontSize: 12, color: token.colorTextSecondary || 'var(--text-secondary)' }}>
                        Multiple alternatives are available for this location.
                      </div>
                    )}
                    {selectedRegion.proposals.map((proposal) => {
                      const proposalId = String(proposal.proposal_id || '');
                      const checked = resolvedAcceptedProposalIds.has(proposalId);
                      const reason = String(proposal.reason || proposal.audit_reason || '').trim();
                      return (
                        <div
                          key={proposalId}
                          style={{
                            border: `1px solid ${proposalId === selectedProposalId
                              ? (token.colorPrimaryBorder || token.colorPrimary || '#1677ff')
                              : (token.colorBorderSecondary || 'var(--border-subtle)')}`,
                            borderRadius: 10,
                            padding: 10,
                            marginBottom: 10,
                            background: proposalId === selectedProposalId
                              ? (token.colorPrimaryBg || 'rgba(22, 119, 255, 0.12)')
                              : (token.colorBgContainer || 'transparent'),
                          }}
                        >
                          <strong>Suggested text</strong>
                          <div
                            style={{
                              marginTop: 6,
                              padding: 10,
                              borderRadius: 8,
                              background: token.colorBgElevated || token.colorBgContainer || 'var(--surface-base)',
                              border: `1px solid ${token.colorBorderSecondary || 'var(--border-subtle)'}`,
                            }}
                          >
                            {selectedRegion.hasStructuralHtml ? (
                              <div dangerouslySetInnerHTML={renderReviewHtml(String(proposal.target_text || ''))} />
                            ) : (
                              formatReviewDisplayText(String(proposal.target_text || ''))
                            )}
                          </div>
                          {reason && (
                            <div style={{ marginTop: 8, color: token.colorTextSecondary || 'var(--text-secondary)', lineHeight: 1.55 }}>
                              <strong>Reason:</strong> {reason}
                            </div>
                          )}
                          <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
                            <input
                              type={selectedRegion.proposals.length > 1 ? 'radio' : 'checkbox'}
                              name={`preview-${selectedRegion.groupId}`}
                              data-testid={`proposal-preview-${proposalId}`}
                              checked={resolvedPreviewProposalIds.has(proposalId)}
                              style={{ accentColor: token.colorPrimary || '#1677ff' }}
                              onChange={(event) => {
                                toggleProposalPreview(proposalId, event.target.checked);
                              }}
                            />
                            <span>Preview</span>
                          </label>
                          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                            <Button
                              type={checked ? 'default' : 'primary'}
                              onClick={() => acceptRegionProposal(selectedRegion, proposalId)}
                            >
                              {checked ? 'Accepted' : 'Accept'}
                            </Button>
                            <Button onClick={() => rejectRegion(selectedRegion)}>
                              Reject
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                </div>
              </div>
              )}
            </div>
          )}
          {!proposalLoading && !proposalError && (reviewRegions.length > 0 || hasUnmappedReviewableChanges) && (
            <div className="proofread-review-save-bar" aria-live="polite">
              <span>
                {hasUnsavedReviewDecisions
                  ? `${acceptedRegionCount} accepted · ${unacceptedRegionCount} not accepted · Draft saved on this device`
                  : `${acceptedRegionCount} accepted · ${unacceptedRegionCount} not accepted`}
              </span>
              <span data-testid="tutorial:proofread-review:save" style={{ display: 'inline-flex' }}>
                <Button
                  type="primary"
                  className={hasUnsavedReviewDecisions ? 'pulse-save-soft' : undefined}
                  onClick={handleSaveAcceptedChangesClick}
                  data-testid="apply-accepted-proposals"
                  loading={isApplyingProposals}
                  disabled={proposalLoading || Boolean(proposalError) || isDiscardingProposalReview}
                >
                  Save Accepted Changes
                </Button>
              </span>
            </div>
          )}
          <Modal
            className="proofread-review-confirm-modal"
            open={isSaveAcceptedConfirmationOpen}
            title="Save accepted changes?"
            footer={(
              <div className="proofread-review-confirm-footer">
                <Button
                  onClick={() => setIsSaveAcceptedConfirmationOpen(false)}
                  disabled={isApplyingProposals}
                >
                  Keep Reviewing
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    void handleApplyAcceptedChanges();
                  }}
                  loading={isApplyingProposals}
                >
                  Save Accepted Changes
                </Button>
              </div>
            )}
            onCancel={() => setIsSaveAcceptedConfirmationOpen(false)}
          >
            <div>
              {`This will apply the accepted suggestions and discard ${unacceptedRegionCount} unaccepted suggestion${unacceptedRegionCount === 1 ? '' : 's'}.`}
            </div>
          </Modal>
        </div>
      );
  } else if (proofreadText) {
      const proofreadHeaderActions = (
        <div className="chapter-content-actions">
          {proofreadStaleBadge}
          {proofreadMetaRows.length > 0 && (
            <Tooltip title={proofreadMetaTooltipTitle}>
              <Button
                aria-label="Proofread details"
                icon={<InfoCircleOutlined />}
              />
            </Tooltip>
          )}

          <Tooltip title="Copy Proofread Content to the Clipboard">
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={() => copyProofreadToClipboard(proofreadText)}
            />
          </Tooltip>
          <Tooltip title="Delete this Chapter's Proofreading">
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteSingleProofreadChapter(sourceLang, targetLang)}
            />
          </Tooltip>
        </div>
      );
      proofreadContent = (
        <div>
          {tabActionsContainer && isActive ? createPortal(proofreadHeaderActions, tabActionsContainer) : null}
          {adminProofreadTracePanel}
          {shouldTreatAsNoChangeProofreadPass ? (
            <div
              style={{
                marginBottom: 12,
                padding: '10px 12px',
                borderRadius: 10,
                border: `1px solid ${token.colorBorderSecondary || 'var(--border-subtle)'}`,
                background: token.colorFillAlter || 'rgba(148, 163, 184, 0.10)',
                color: token.colorTextSecondary || 'var(--text-secondary)',
              }}
            >
              {noChangeProofreadMessage}
            </div>
          ) : null}
          <div className="chapter-content-toolbar">
            <div data-testid="tutorial:proofread-view:controls">
              <Radio.Group
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
              >
                <Radio.Button value="sideBySide">
                  <span data-testid="tutorial:proofread-view:side-by-side" onClick={() => setViewMode('sideBySide')}>Side-by-Side View</span>
                </Radio.Button>
                <Radio.Button value="inlineDiff">
                  <span data-testid="tutorial:proofread-view:inline" onClick={() => setViewMode('inlineDiff')}>In-Line View</span>
                </Radio.Button>
                <Radio.Button value="proofreadOnly">
                  <span data-testid="tutorial:proofread-view:proofread-only" onClick={() => setViewMode('proofreadOnly')}>Proofread Only</span>
                </Radio.Button>
              </Radio.Group>
            </div>

            {!tabActionsContainer && proofreadHeaderActions}
          </div>

          {viewMode === 'sideBySide' && (
            <div
              className="chapter-proofread-compare"
              data-tour-id="chapter-content"
              data-testid="tutorial:proofread-view:side-by-side-content"
            >
              {/* Side-by-Side Comparison */}
              <div className="chapter-proofread-panel">
                <h3>Original Content</h3>
                <div
                  style={contentStyle}
                  className="chapter-proofread-content"
                  id="leftPanel"
                  onScroll={(e) => syncScroll(e, 'rightPanel')}
                  dangerouslySetInnerHTML={{ __html: removeHyperlinks(originalText) }}
                />
              </div>
              <div className="chapter-proofread-panel">
                <h3>Proofread Content</h3>
                <div
                  style={contentStyle}
                  className="chapter-proofread-content"
                  id="rightPanel"
                  onScroll={(e) => syncScroll(e, 'leftPanel')}
                  dangerouslySetInnerHTML={{ __html: removeHyperlinks(proofreadText) }}
                />
              </div>
            </div>
          )}

          {viewMode === 'inlineDiff' && (
            <div
              className="chapter-proofread-panel chapter-proofread-panel--single"
              data-tour-id="chapter-content"
              data-testid="tutorial:proofread-view:inline-content"
            >
              <h3>
                In-Line View
                <Tooltip
                  title={[
                    "Added text is highlighted in green, removed text in pink.\n",
                    "Some normally hidden formatting elements may be lost or exposed in this view.\n",
                    "Additionally, formatting such as bold, italics, alignment, etc., will not be displayed."
                  ].join('\n')}
                >
                  <QuestionCircleOutlined
                    style={{ marginLeft: '5px', color: 'var(--text-danger, #ef4444)' }}
                  />
                </Tooltip>
              </h3>
              <div
                style={contentStyle}
                className="chapter-proofread-content"
                dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(renderDiff(removeHyperlinks(originalText), removeHyperlinks(proofreadText))) }}
              />
            </div>
          )}
          {viewMode === 'proofreadOnly' && (() => {
            const shouldShowOrientation =
              !!proofreadText &&
              (isJapaneseLanguage(targetLang) || containsJapaneseCharacters(proofreadText));
            const proofreadOnlyContentStyle: CSSProperties =
              shouldShowOrientation && writingDirection === 'vertical'
                ? {
                    ...contentStyle,
                    writingMode: 'vertical-rl',
                    textOrientation: 'upright',
                    overflowX: 'auto',
                    padding: '10px 14px',
                  }
                : contentStyle;
            return (
              <div
                className="chapter-proofread-panel chapter-proofread-panel--single"
                data-tour-id="chapter-content"
                data-testid="tutorial:proofread-view:proofread-only-content"
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    marginBottom: '10px',
                    gap: 10,
                  }}
                >
                  <h3 className="chapter-proofread-section-title">Proofread Content</h3>
                  {shouldShowOrientation && (
                    <Segmented
                      size="small"
                      value={writingDirection}
                      onChange={(value) => setWritingDirection(value as 'horizontal' | 'vertical')}
                      options={[
                        { label: 'Horizontal (横書き)', value: 'horizontal' },
                        { label: 'Vertical (縦書き)', value: 'vertical' },
                      ]}
                      aria-label="Proofread text orientation"
                    />
                  )}
                </div>
                <div
                  style={proofreadOnlyContentStyle}
                  className="chapter-proofread-content"
                  dangerouslySetInnerHTML={{ __html: removeHyperlinks(proofreadText) }}
                />
              </div>
            );
          })()}
        </div>
      );
  }

  if (selectedChapter) {
    let proofreadComments = "";

    if (sourceLang === "Original") {
      proofreadComments = selectedChapter.proofread_comments;
    } else {
      if (translatedChapter && translatedChapter.proofread_comments) {
        proofreadComments = translatedChapter.proofread_comments;
      }
    }

    if (proofreadComments) {
      editingNotesContent = (
        <div className="chapter-editing-notes-panel">
          <div className="chapter-editing-notes-header">
            <div>
              <h3>Editing Notes</h3>
              <p>Chapter-level editorial guidance generated for this source text.</p>
            </div>
            <Button
              danger
              size="small"
              onClick={() => deleteEditingNotes(selectedChapter.chapter_guid, sourceLang, targetLang)}
            >
              Delete Editing Notes
            </Button>
          </div>
          <div
            style={contentStyle}
            className="chapter-editing-notes-content"
            dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(proofreadComments) }}
          />
        </div>
      );
    }
  }

  const hasEditingNotes = Boolean(editingNotesContent);

  if (!proofreadContent) {
    isProofread = false;
    proofreadContent = (
      <Tooltip title="This chapter has not been proofread yet.">
        <span className="chapter-empty-state">
          <span>
            <ExclamationCircleOutlined /> This chapter is not proofread yet.
          </span>
          {onOpenProofreadModal && (
            <span className="chapter-empty-state__actions">
              <Button
                size="small"
                type="primary"
                onClick={() => onOpenProofreadModal({
                  sourceLang,
                  targetLang,
                  proofread: true,
                  editingNotes: false,
                })}
              >
                Proofread this chapter
              </Button>
              <a className="proofread-context-help" href="/support?tab=articles&article=proofread-a-chapter">
                <QuestionCircleOutlined /> How proofreading works
              </a>
            </span>
          )}
        </span>
      </Tooltip>
    );
  }

  if (!editingNotesContent) {
    editingNotesContent = (
      <Tooltip title="This chapter has no editing notes yet.">
        <span className="chapter-empty-state">
          <span>
            <ExclamationCircleOutlined /> This chapter has no editing notes yet.
          </span>
          {onOpenProofreadModal && sourceLang === 'Original' && (
            <Button
              size="small"
              type="primary"
              onClick={() => onOpenProofreadModal({
                sourceLang,
                targetLang,
                proofread: false,
                editingNotes: true,
              })}
            >
              Generate editing notes
            </Button>
          )}
        </span>
      </Tooltip>
    );
  }

  return {
    content: (
      <div className="chapter-proofread-shell" data-tour-id="chapter-content">
        {proofreadContent}
        {includeEditingNotesInContent && sourceLang === "Original" && (
          <>
            {editingNotesContent}
          </>
        )}
        {!proofreadContent && !editingNotesContent && (
          <Tooltip title="This chapter has not been proofread yet.">
            <span className="chapter-empty-state">
              <ExclamationCircleOutlined /> This chapter is not proofread yet.
            </span>
          </Tooltip>
        )}
      </div>
    ),
    isProofread: isProofread,
    editingNotesContent,
    hasEditingNotes,
  };
};

export default ProofreadTabContent;
